import { useState, useEffect, useRef, useMemo } from "react";

const STORAGE_KEY = "ahcc_data_v2";
const ADMIN_PASS = "herd2025";
const TRUSTED_IPS = ["99.103.219.83"];

// ─── DETECT ADMIN ROUTE ───────────────────────────────────────────────────────
// Admin is only accessible at /admin — never linked from the public site
function isAdminRoute() {
  try {
    const path = window.location.pathname;
    return path === "/admin" || path === "/admin/";
  } catch {
    return false;
  }
}

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC"
];

const STATE_NAMES = {
  AL:"Alabama",AK:"Alaska",AZ:"Arizona",AR:"Arkansas",CA:"California",CO:"Colorado",
  CT:"Connecticut",DE:"Delaware",FL:"Florida",GA:"Georgia",HI:"Hawaii",ID:"Idaho",
  IL:"Illinois",IN:"Indiana",IA:"Iowa",KS:"Kansas",KY:"Kentucky",LA:"Louisiana",
  ME:"Maine",MD:"Maryland",MA:"Massachusetts",MI:"Michigan",MN:"Minnesota",MS:"Mississippi",
  MO:"Missouri",MT:"Montana",NE:"Nebraska",NV:"Nevada",NH:"New Hampshire",NJ:"New Jersey",
  NM:"New Mexico",NY:"New York",NC:"North Carolina",ND:"North Dakota",OH:"Ohio",
  OK:"Oklahoma",OR:"Oregon",PA:"Pennsylvania",RI:"Rhode Island",SC:"South Carolina",
  SD:"South Dakota",TN:"Tennessee",TX:"Texas",UT:"Utah",VT:"Vermont",VA:"Virginia",
  WA:"Washington",WV:"West Virginia",WI:"Wisconsin",WY:"Wyoming",DC:"Washington D.C."
};

const STATE_TZ = {
  AL:"America/Chicago",AK:"America/Anchorage",AZ:"America/Phoenix",AR:"America/Chicago",
  CA:"America/Los_Angeles",CO:"America/Denver",CT:"America/New_York",DE:"America/New_York",
  FL:"America/New_York",GA:"America/New_York",HI:"Pacific/Honolulu",ID:"America/Denver",
  IL:"America/Chicago",IN:"America/Indiana/Indianapolis",IA:"America/Chicago",
  KS:"America/Chicago",KY:"America/Kentucky/Louisville",LA:"America/Chicago",
  ME:"America/New_York",MD:"America/New_York",MA:"America/New_York",MI:"America/Detroit",
  MN:"America/Chicago",MS:"America/Chicago",MO:"America/Chicago",MT:"America/Denver",
  NE:"America/Chicago",NV:"America/Los_Angeles",NH:"America/New_York",NJ:"America/New_York",
  NM:"America/Denver",NY:"America/New_York",NC:"America/New_York",ND:"America/Chicago",
  OH:"America/New_York",OK:"America/Chicago",OR:"America/Los_Angeles",PA:"America/New_York",
  RI:"America/New_York",SC:"America/New_York",SD:"America/Chicago",TN:"America/Chicago",
  TX:"America/Chicago",UT:"America/Denver",VT:"America/New_York",VA:"America/New_York",
  WA:"America/Los_Angeles",WV:"America/New_York",WI:"America/Chicago",WY:"America/Denver",
  DC:"America/New_York"
};

const TZ_ABBR = {
  "America/New_York":"ET","America/Chicago":"CT","America/Denver":"MT",
  "America/Los_Angeles":"PT","America/Phoenix":"MST","America/Anchorage":"AKT",
  "Pacific/Honolulu":"HT","America/Indiana/Indianapolis":"ET",
  "America/Kentucky/Louisville":"ET","America/Detroit":"ET"
};

function getStateTzAbbr(state) {
  return TZ_ABBR[STATE_TZ[state] || "America/Chicago"] || "LT";
}

function parseInStateTz(dateStr, timeStr, state) {
  const tz = STATE_TZ[state] || "America/Chicago";
  const utcGuess = new Date(`${dateStr}T${timeStr || "00:00"}:00Z`);
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz, year:"numeric", month:"2-digit", day:"2-digit",
    hour:"2-digit", minute:"2-digit", second:"2-digit", hour12: false
  });
  const parts = fmt.formatToParts(utcGuess);
  const p = {};
  parts.forEach(({ type, value }) => { p[type] = value; });
  const localRepr = new Date(`${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}Z`);
  return new Date(utcGuess.getTime() + (utcGuess - localRepr));
}

function getTodayInState(state) {
  const tz = STATE_TZ[state] || "America/Chicago";
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: tz, year:"numeric", month:"2-digit", day:"2-digit"
  }).format(new Date());
}

function getSaleStatus(loc) {
  if (loc.soldOut) return "sold_out";
  if (!loc.startDate || !loc.endDate) return "upcoming";
  try {
    const now   = new Date();
    const start = parseInStateTz(loc.startDate, loc.startTime || "00:00", loc.state);
    const end   = parseInStateTz(loc.endDate,   loc.endTime   || "23:59", loc.state);
    if (now < start) return "upcoming";
    if (now > end)   return "ended";
    const today = getTodayInState(loc.state);
    if (today === loc.startDate) return "grand_opening";
    if (today === loc.endDate)   return "last_day";
    return "active";
  } catch { return "upcoming"; }
}

const STATUS_CFG = {
  upcoming:      { label:"Coming Soon",    ribbon:null,                 ribbonBg:"",                      ribbonColor:"",        badgeBg:"rgba(13,27,62,0.92)",    badgeBorder:"rgba(42,66,120,0.7)",   badgeColor:"#a8bfe8" },
  grand_opening: { label:"Grand Opening!", ribbon:"🎉 GRAND OPENING",   ribbonBg:"rgba(15,110,15,0.96)",  ribbonColor:"#a0f0a0", badgeBg:"rgba(10,80,10,0.92)",    badgeBorder:"rgba(80,200,80,0.7)",   badgeColor:"#7fd87f" },
  active:        { label:"Open Now",       ribbon:null,                 ribbonBg:"",                      ribbonColor:"",        badgeBg:"rgba(13,27,62,0.88)",    badgeBorder:"rgba(42,66,120,0.6)",   badgeColor:"#a8bfe8" },
  last_day:      { label:"Last Day!",      ribbon:"⚡ LAST DAY",         ribbonBg:"rgba(150,70,0,0.96)",   ribbonColor:"#ffd080", badgeBg:"rgba(140,60,0,0.92)",    badgeBorder:"rgba(230,130,0,0.7)",   badgeColor:"#ffc46b" },
  sold_out:      { label:"Sold Out",       ribbon:"✖ SOLD OUT",         ribbonBg:"rgba(100,8,20,0.97)",   ribbonColor:"#ffaaaa", badgeBg:"rgba(80,10,10,0.95)",    badgeBorder:"rgba(200,16,46,0.8)",   badgeColor:"#f08080" },
  ended:         { label:"Sale Ended",     ribbon:null,                 ribbonBg:"",                      ribbonColor:"",        badgeBg:"rgba(20,20,20,0.92)",    badgeBorder:"rgba(80,80,80,0.5)",    badgeColor:"#555"    },
};

const defaultData = {
  logo: null, slides: [],
  locations: [
    { id:1, city:"Birmingham", state:"AL", address:"123 Main St, Birmingham, AL 35201", startDate:"2025-05-17", endDate:"2025-05-18", startTime:"09:00", endTime:"18:00", notes:"Walmart parking lot on Hwy 280", storeImage:null, soldOut:false },
    { id:2, city:"Los Angeles", state:"CA", address:"789 Sunset Blvd, Los Angeles, CA 90028", startDate:"2025-05-15", endDate:"2025-05-17", startTime:"09:00", endTime:"17:00", notes:"Sam's Club east lot, look for white tents", storeImage:null, soldOut:false },
    { id:3, city:"Chicago", state:"IL", address:"555 Michigan Ave, Chicago, IL 60611", startDate:"2025-06-14", endDate:"2025-06-15", startTime:"08:00", endTime:"17:00", notes:"Near Millennium Park — follow the signs", storeImage:null, soldOut:false },
  ],
  about: "We are a premium American beef operation bringing farm-fresh, USDA-inspected frozen meats directly to your community. Our products are sourced from carefully selected ranches across the heartland of America.",
  contact: { email:"info@americanherdcattleco.com", phone:"(800) 555-0100", address:"1053 Commerce Blvd, Pelham, AL 35124" },
  faqs: [
    { q:"Are your products USDA inspected?", a:"Yes — all products are USDA-inspected and sourced from certified facilities." },
    { q:"Do you accept credit cards?", a:"Yes, we accept all major credit cards, cash, and contactless payments." },
    { q:"Are the products already frozen?", a:"Yes, all products arrive fully frozen and vacuum-sealed for freshness." },
  ],
};

function loadData() { return defaultData; }
function saveData(d) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(d));
  fetch('/api/data', { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(d) }).catch(()=>{});
}
async function loadRemoteData() {
  try {
    const r = await fetch('/api/data');
    const d = await r.json();
    if (d) return { ...defaultData, ...d };
  } catch {}
  try { const r = localStorage.getItem(STORAGE_KEY); if (r) return { ...defaultData, ...JSON.parse(r) }; } catch {}
  return defaultData;
}
function mapsUrl(addr) { return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`; }

function fmtDateRange(loc) {
  if (!loc.startDate) return "";
  const mo = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const fmt = d => { const [,m,day] = d.split("-"); return `${mo[+m-1]} ${+day}`; };
  return loc.startDate === loc.endDate ? fmt(loc.startDate) : `${fmt(loc.startDate)} – ${fmt(loc.endDate)}`;
}
function fmtTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM", h12 = h % 12 || 12;
  return m === 0 ? `${h12}${ap}` : `${h12}:${String(m).padStart(2,"0")}${ap}`;
}

// ─── STYLES ──────────────────────────────────────────────────────────────────
const S = () => <style>{`
@media(max-width:768px){.nav-burger{display:flex!important;background:none;border:none;color:#f0ece2;font-size:1.6rem;cursor:pointer;padding:.5rem;margin-left:auto;line-height:1}.nav-links{display:none!important}.nav-logo-text{font-size:.78rem!important;white-space:nowrap!important}}
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=Barlow:wght@300;400;500;600&family=Barlow+Condensed:wght@500;600;700&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
body{font-family:'Barlow',sans-serif;background:#0a0f1e;color:#f0ece2}
:root{--red:#c8102e;--redl:#e8213e;--navy:#0d1b3e;--navym:#1a2f5a;--navyl:#2a4278;--cream:#f0ece2;--creamd:#d4cdb8}
.site{min-height:100vh;background:#0a0f1e;position:relative}
.flag-bg{position:fixed;top:0;left:0;right:0;height:100vh;z-index:0;pointer-events:none;overflow:hidden}
.flag-bg video{position:absolute;top:0;left:0;width:100%;height:100%;object-fit:cover;mix-blend-mode:screen;opacity:.13;filter:saturate(1.3) brightness(.8)}
.flag-bg::after{content:'';position:absolute;inset:0;background:linear-gradient(180deg,rgba(10,15,30,.2) 0%,rgba(10,15,30,.6) 55%,rgba(10,15,30,.97) 80%,rgba(10,15,30,1) 100%);pointer-events:none}
.flag-bg svg{width:100%;height:100%;opacity:.045}
@keyframes waveflag{0%{transform:skewX(0deg) scaleY(1)}25%{transform:skewX(1.5deg) scaleY(1.02)}50%{transform:skewX(0deg) scaleY(.98)}75%{transform:skewX(-1.5deg) scaleY(1.02)}100%{transform:skewX(0deg) scaleY(1)}}
.flag-cloth{transform-origin:left center;animation:waveflag 6s ease-in-out infinite}
@keyframes wavestripe{0%,100%{d:path("M0,H L W,H")}50%{d:path("M0,H Q W2,Hm W,H")}}
.site>*:not(.flag-bg){position:relative;z-index:1}
.nav{position:fixed;top:0;left:0;right:0;z-index:100;background:rgba(10,15,30,0.93);backdrop-filter:blur(12px);border-bottom:1px solid rgba(200,16,46,0.3);padding:0 2rem;height:72px;display:flex;align-items:center;justify-content:space-between;animation:borderPulse 4s ease-in-out infinite}
.nav-logo{height:50px;display:flex;align-items:center;cursor:pointer}
.nav-logo img{height:50px;object-fit:contain}
.nav-logo-text{font-family:'Playfair Display',serif;font-size:1.05rem;font-weight:700;color:var(--cream);letter-spacing:.05em;white-space:nowrap}
.nav-burger{display:none;background:none;border:none;color:var(--cream);font-size:1.5rem;cursor:pointer;padding:.4rem;line-height:1;z-index:101}
.nav-links{display:flex;gap:2rem;list-style:none;align-items:center}
.nav-links a{color:var(--creamd);font-size:.82rem;font-weight:500;letter-spacing:.12em;text-transform:uppercase;text-decoration:none;transition:color .2s;cursor:pointer}
.nav-links a:hover,.nav-links a.on{color:var(--redl)}
.hero{position:relative;height:52vh;min-height:320px;overflow:hidden;background:var(--navy)}
.sl{position:absolute;inset:0;opacity:0;transition:opacity 1.2s ease}
.sl.on{opacity:1}
.sl img{width:100%;height:100%;object-fit:cover}
.sl-ov{position:absolute;inset:0;background:linear-gradient(135deg,rgba(10,15,30,.87) 0%,rgba(13,27,62,.55) 55%,rgba(200,16,46,.1) 100%)}
.hero-glow{position:absolute;inset:0;pointer-events:none;overflow:hidden}
.hero-glow::before{content:'';position:absolute;bottom:-20%;left:50%;transform:translateX(-50%);width:80%;height:60%;background:radial-gradient(ellipse,rgba(200,16,46,.12) 0%,transparent 70%);animation:glowPulse 5s ease-in-out infinite alternate}
.hero-glow::after{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:linear-gradient(90deg,transparent,rgba(200,16,46,.6),rgba(232,33,62,.8),rgba(200,16,46,.6),transparent);animation:scanLine 4s ease-in-out infinite alternate}
@keyframes glowPulse{0%{opacity:.4;transform:translateX(-50%) scaleX(1)}100%{opacity:1;transform:translateX(-50%) scaleX(1.2)}}
@keyframes scanLine{0%{opacity:.3;transform:scaleX(.6)}100%{opacity:.8;transform:scaleX(1)}}
.hero-ey{animation:fadeUp .8s ease both}
.hero-h{animation:fadeUp 1s .15s ease both}
.hero-s{animation:fadeUp 1s .3s ease both}
.hbtn{animation:fadeUp 1s .45s ease both}
@keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
.nav{transition:box-shadow .3s}
@keyframes borderPulse{0%,100%{border-bottom-color:rgba(200,16,46,.3)}50%{border-bottom-color:rgba(200,16,46,.7)}}
.nav{animation:borderPulse 4s ease-in-out infinite}
.hbtn{position:relative;overflow:hidden}
.hbtn::after{content:'';position:absolute;top:0;left:-100%;width:100%;height:100%;background:linear-gradient(90deg,transparent,rgba(255,255,255,.12),transparent);animation:btnShimmer 3s ease-in-out infinite}
@keyframes btnShimmer{0%{left:-100%}100%{left:200%}}
.lc-card{transition:transform .25s ease,box-shadow .25s ease}
.lc-card:hover{transform:translateY(-4px);box-shadow:0 12px 32px rgba(200,16,46,.15)}
.hero-empty{display:none}
.hero-c{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:0 4rem;text-align:center}
.hero-ey{font-family:'Barlow Condensed',sans-serif;font-size:.82rem;letter-spacing:.3em;text-transform:uppercase;color:var(--redl);margin-bottom:1rem;display:flex;align-items:center;gap:.75rem}
.hero-ey::before{content:'';display:block;width:40px;height:1px;background:var(--redl)}
.hero-h{font-family:'Playfair Display',serif;font-size:clamp(1.6rem,3.5vw,3rem);font-weight:900;line-height:1.05;color:var(--cream);max-width:680px;margin-bottom:1rem}
.hero-s{font-size:.9rem;font-weight:300;color:var(--creamd);max-width:480px;margin-bottom:1.5rem;line-height:1.7}
.hbtn{display:inline-flex;align-items:center;background:var(--red);color:white;padding:.9rem 2rem;font-family:'Barlow Condensed',sans-serif;font-size:.9rem;font-weight:600;letter-spacing:.15em;text-transform:uppercase;border:none;cursor:pointer;transition:background .2s}
.hbtn:hover{background:var(--redl)}
.hbtn-o{background:transparent;border:1px solid rgba(240,236,226,.35);color:var(--cream)}
.hbtn-o:hover{background:rgba(240,236,226,.07);border-color:var(--cream)}
.sl-dots{position:absolute;bottom:2rem;left:50%;transform:translateX(-50%);display:flex;gap:.5rem}
.sl-dot{width:8px;height:8px;border-radius:50%;background:rgba(240,236,226,.3);border:none;cursor:pointer;transition:background .3s,transform .3s}
.sl-dot.on{background:var(--redl);transform:scale(1.3)}
.sec{padding:6rem 4rem;max-width:1200px;margin:0 auto}
.sec-lbl{font-family:'Barlow Condensed',sans-serif;font-size:.78rem;letter-spacing:.3em;text-transform:uppercase;color:var(--redl);margin-bottom:.75rem;display:flex;align-items:center;gap:.75rem}
.sec-lbl::before{content:'';display:block;width:30px;height:1px;background:var(--redl)}
.sec-title{font-family:'Playfair Display',serif;font-size:clamp(1.8rem,3vw,2.8rem);font-weight:700;color:var(--cream);margin-bottom:1rem}
.sec-desc{color:var(--creamd);font-weight:300;line-height:1.8;max-width:640px;margin-bottom:2.5rem}
.sf-wrap{display:flex;align-items:flex-start;gap:1rem;margin-bottom:2rem;flex-wrap:wrap}
.sf-label{font-family:'Barlow Condensed',sans-serif;font-size:.72rem;letter-spacing:.2em;text-transform:uppercase;color:var(--creamd);white-space:nowrap;padding-top:.45rem}
.sf-tabs{display:flex;gap:.45rem;flex-wrap:wrap}
.sf-tab{background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.09);color:var(--creamd);padding:.3rem .8rem;font-family:'Barlow Condensed',sans-serif;font-size:.72rem;font-weight:700;letter-spacing:.1em;text-transform:uppercase;cursor:pointer;border-radius:2px;transition:all .15s}
.sf-tab:hover{border-color:rgba(200,16,46,.4);color:var(--cream)}
.sf-tab.on{background:var(--red);border-color:var(--red);color:white}
.legend{display:flex;flex-wrap:wrap;gap:.75rem;margin-bottom:2rem}
.leg{display:flex;align-items:center;gap:.35rem;font-size:.68rem;letter-spacing:.08em;text-transform:uppercase;color:rgba(240,236,226,.45)}
.leg-dot{width:7px;height:7px;border-radius:50%;flex-shrink:0}
.loc-bg{background:var(--navy)}
.loc-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(310px,1fr));gap:1.5rem}
.lc{background:rgba(255,255,255,.025);border:1px solid rgba(255,255,255,.08);border-radius:4px;overflow:hidden;position:relative;display:flex;flex-direction:column;transition:border-color .2s,transform .2s}
.lc:hover{border-color:rgba(200,16,46,.4);transform:translateY(-3px)}
.lc.so{opacity:.65}
.lc.ended{opacity:.45}
.lc-img{position:relative;height:175px;overflow:hidden;background:linear-gradient(135deg,#0d1b3e,#182f5a);flex-shrink:0}
.lc-img img{width:100%;height:100%;object-fit:cover;display:block}
.lc-img-ph{display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;gap:.5rem}
.lc-img-ph-i{font-size:2.5rem;opacity:.2}
.lc-img-ph-t{font-size:.65rem;letter-spacing:.15em;text-transform:uppercase;opacity:.25}
.ribbon{position:absolute;top:14px;left:0;font-family:'Barlow Condensed',sans-serif;font-size:.68rem;font-weight:700;letter-spacing:.18em;text-transform:uppercase;padding:.28rem 1rem .28rem .7rem;clip-path:polygon(0 0,100% 0,calc(100% - 8px) 50%,100% 100%,0 100%);z-index:2}
.badge{position:absolute;bottom:9px;right:9px;font-family:'Barlow Condensed',sans-serif;font-size:.65rem;font-weight:700;letter-spacing:.12em;text-transform:uppercase;padding:.22rem .55rem;border-radius:2px;backdrop-filter:blur(8px)}
.lc-body{padding:1.1rem 1.25rem 0;flex:1}
.lc-city-row{display:flex;align-items:baseline;gap:.5rem;margin-bottom:.25rem}
.lc-city{font-family:'Barlow Condensed',sans-serif;font-size:1.25rem;font-weight:700;color:var(--cream);letter-spacing:.04em}
.lc-spill{font-family:'Barlow Condensed',sans-serif;font-size:.65rem;font-weight:700;letter-spacing:.1em;color:var(--redl);background:rgba(200,16,46,.1);border:1px solid rgba(200,16,46,.22);padding:.12rem .45rem;border-radius:2px}
.lc-addr{font-size:.77rem;color:rgba(240,236,226,.45);margin-bottom:.65rem;line-height:1.4}
.lc-meta{display:flex;flex-direction:column;gap:.28rem;margin-bottom:.65rem}
.lc-mr{display:flex;align-items:flex-start;gap:.45rem;font-size:.8rem}
.lc-mi{font-size:.8rem;width:16px;text-align:center;flex-shrink:0;margin-top:1px}
.lc-mt{color:var(--creamd);font-weight:400;line-height:1.4}
.lc-mt strong{color:var(--cream);font-weight:500}
.lc-act{padding:.7rem 1.1rem 1.1rem;display:flex;gap:.55rem;border-top:1px solid rgba(255,255,255,.05);margin-top:auto}
.lc-btn{flex:1;padding:.5rem 0;font-family:'Barlow Condensed',sans-serif;font-size:.75rem;font-weight:700;letter-spacing:.12em;text-transform:uppercase;border:none;cursor:pointer;border-radius:2px;transition:all .15s;display:flex;align-items:center;justify-content:center;gap:.35rem;text-decoration:none}
.lc-dir{background:var(--red);color:white}
.lc-dir:hover{background:var(--redl)}
.lc-dir.dis{background:rgba(100,100,100,.25);color:rgba(240,236,226,.25);cursor:not-allowed;pointer-events:none}
.no-loc{color:var(--creamd);text-align:center;padding:4rem 0;font-style:italic}
.ab-bg{background:#0d1220}
.ab-in{display:grid;grid-template-columns:1fr 1fr;gap:5rem;align-items:center;max-width:1200px;margin:0 auto;padding:6rem 4rem}
.ab-box{background:var(--navy);border:1px solid rgba(200,16,46,.18);padding:2.5rem}
.ab-stat{font-family:'Playfair Display',serif;font-size:2.8rem;font-weight:900;color:var(--redl)}
.ab-stat-l{font-size:.72rem;letter-spacing:.18em;text-transform:uppercase;color:var(--creamd);margin-bottom:1.75rem}
.ab-div{width:100%;height:1px;background:rgba(200,16,46,.18);margin:1.25rem 0}
.faq-item{border-bottom:1px solid rgba(240,236,226,.07);padding:1.2rem 0;cursor:pointer}
.faq-q{font-weight:500;color:var(--cream);display:flex;justify-content:space-between;align-items:center;gap:1rem}
.faq-ic{color:var(--redl);font-size:1.2rem;flex-shrink:0;transition:transform .3s}
.faq-ic.op{transform:rotate(45deg)}
.faq-a{font-size:.87rem;color:var(--creamd);line-height:1.7;font-weight:300;margin-top:.7rem;padding-right:2rem}
.ct-bg{background:var(--navy)}
.ct-in{max-width:1200px;margin:0 auto;padding:6rem 4rem;display:grid;grid-template-columns:1fr 1fr;gap:5rem}
.ct-item{display:flex;align-items:flex-start;gap:1rem;margin-bottom:1.75rem}
.ct-icon{width:40px;height:40px;background:rgba(200,16,46,.1);border:1px solid rgba(200,16,46,.22);display:flex;align-items:center;justify-content:center;font-size:1.1rem;flex-shrink:0}
.ct-lbl{font-size:.68rem;letter-spacing:.15em;text-transform:uppercase;color:var(--creamd);margin-bottom:.2rem}
.ct-val{color:var(--cream)}
.ft{background:#060910;border-top:1px solid rgba(200,16,46,.12);padding:1.75rem 4rem;display:flex;align-items:center;justify-content:space-between}
.ft-copy{font-size:.75rem;color:rgba(240,236,226,.3);letter-spacing:.04em}
/* ADMIN */
.adm{position:fixed;inset:0;background:#07090f;z-index:200;overflow-y:auto;font-family:'Barlow',sans-serif}
.adm-top{background:#0d1220;border-bottom:1px solid rgba(200,16,46,.22);padding:0 2rem;height:60px;display:flex;align-items:center;justify-content:space-between;position:sticky;top:0;z-index:10}
.adm-top-t{font-family:'Barlow Condensed',sans-serif;font-size:1rem;font-weight:700;color:var(--cream);letter-spacing:.12em;text-transform:uppercase}
.adm-top-t span{color:var(--redl)}
.adm-side{width:215px;background:#0d1220;border-right:1px solid rgba(255,255,255,.05);padding:1.25rem 0;position:fixed;top:60px;bottom:0;overflow-y:auto}
.adm-item{padding:.58rem 1.4rem;font-size:.8rem;font-weight:500;color:rgba(240,236,226,.42);cursor:pointer;letter-spacing:.04em;transition:all .15s;display:flex;align-items:center;gap:.55rem;border-left:3px solid transparent}
.adm-item:hover{color:var(--cream);background:rgba(255,255,255,.02)}
.adm-item.on{color:var(--redl);border-left-color:var(--redl);background:rgba(200,16,46,.05)}
.adm-c{margin-left:215px;padding:2rem;min-height:calc(100vh - 60px)}
.adm-p{max-width:900px}
.adm-t{font-family:'Playfair Display',serif;font-size:1.3rem;color:var(--cream);margin-bottom:.2rem}
.adm-s{font-size:.8rem;color:rgba(240,236,226,.32);margin-bottom:2rem}
.acard{background:#0d1220;border:1px solid rgba(255,255,255,.07);border-radius:4px;padding:1.4rem;margin-bottom:1.4rem}
.acard-l{font-family:'Barlow Condensed',sans-serif;font-size:.68rem;font-weight:700;letter-spacing:.15em;text-transform:uppercase;color:var(--redl);margin-bottom:.9rem}
.al{font-size:.68rem;font-weight:600;letter-spacing:.1em;text-transform:uppercase;color:rgba(240,236,226,.4);margin-bottom:.3rem;display:block;margin-top:.7rem}
.al:first-child{margin-top:0}
.ai{width:100%;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.09);color:var(--cream);padding:.52rem .72rem;font-family:'Barlow',sans-serif;font-size:.87rem;border-radius:3px}
.ai:focus{outline:none;border-color:rgba(200,16,46,.5)}
.ai option{background:#0d1b3e}
.ata{min-height:90px;resize:vertical}
.ar{display:grid;grid-template-columns:1fr 1fr;gap:1rem}
.btn-r{background:var(--red);color:white;border:none;padding:.52rem 1.2rem;font-family:'Barlow Condensed',sans-serif;font-size:.8rem;font-weight:700;letter-spacing:.12em;text-transform:uppercase;cursor:pointer;border-radius:2px;transition:background .15s}
.btn-r:hover{background:var(--redl)}
.btn-g{background:transparent;color:var(--creamd);border:1px solid rgba(255,255,255,.12);padding:.46rem .95rem;font-family:'Barlow Condensed',sans-serif;font-size:.75rem;font-weight:700;letter-spacing:.1em;text-transform:uppercase;cursor:pointer;border-radius:2px;transition:all .15s}
.btn-g:hover{border-color:rgba(255,255,255,.28);color:var(--cream)}
.btn-d{background:transparent;color:#e25555;border:1px solid rgba(226,85,85,.28);padding:.36rem .72rem;font-size:.7rem;font-family:'Barlow Condensed',sans-serif;font-weight:700;letter-spacing:.1em;text-transform:uppercase;cursor:pointer;border-radius:2px;transition:all .15s}
.btn-d:hover{background:rgba(226,85,85,.08);border-color:rgba(226,85,85,.55)}
.btn-w{background:transparent;color:#e8a020;border:1px solid rgba(232,160,32,.32);padding:.36rem .72rem;font-size:.7rem;font-family:'Barlow Condensed',sans-serif;font-weight:700;letter-spacing:.1em;text-transform:uppercase;cursor:pointer;border-radius:2px;transition:all .15s}
.btn-w:hover{background:rgba(232,160,32,.08)}
.btn-ok{background:transparent;color:#5cb85c;border:1px solid rgba(92,184,92,.32);padding:.36rem .72rem;font-size:.7rem;font-family:'Barlow Condensed',sans-serif;font-weight:700;letter-spacing:.1em;text-transform:uppercase;cursor:pointer;border-radius:2px;transition:all .15s}
.btn-ok:hover{background:rgba(92,184,92,.08)}
.alc{background:rgba(255,255,255,.02);border:1px solid rgba(255,255,255,.07);border-radius:3px;padding:.9rem 1.1rem;margin-bottom:.65rem}
.alc-top{display:flex;justify-content:space-between;align-items:flex-start;gap:1rem;margin-bottom:.6rem}
.alc-title{font-weight:600;color:var(--cream);font-size:.92rem;display:flex;align-items:center;gap:.55rem}
.alc-sub{font-size:.75rem;color:rgba(240,236,226,.38);margin-top:.12rem}
.alc-acts{display:flex;gap:.45rem;flex-wrap:wrap;margin-top:.6rem}
.upzone{border:2px dashed rgba(200,16,46,.28);border-radius:4px;padding:1.6rem;text-align:center;cursor:pointer;transition:border-color .2s;background:rgba(200,16,46,.02)}
.upzone:hover{border-color:rgba(200,16,46,.52)}
.up-dim{font-size:.68rem;color:rgba(200,16,46,.5);margin-top:.2rem;font-weight:600;letter-spacing:.05em}
.login-sc{display:flex;align-items:center;justify-content:center;min-height:100vh;background:#07090f}
.login-bx{background:#0d1220;border:1px solid rgba(200,16,46,.2);padding:2.5rem;width:340px;border-radius:4px}
.login-t{font-family:'Playfair Display',serif;font-size:1.35rem;color:var(--cream);margin-bottom:.2rem}
.login-s{font-size:.75rem;color:rgba(240,236,226,.38);margin-bottom:1.75rem}
.toasts{position:fixed;bottom:2rem;right:2rem;z-index:9999;display:flex;flex-direction:column;gap:.45rem}
.toast{background:#1a3a1a;border:1px solid #2d6a2d;color:#7fd87f;padding:.6rem 1rem;border-radius:3px;font-size:.8rem;animation:fu .3s ease}
@keyframes fu{from{opacity:0;transform:translateY(7px)}to{opacity:1;transform:translateY(0)}}
.pc{padding-top:72px}
@media(max-width:768px){
  .nav-links{gap:1rem}
  .ab-in,.ct-in{grid-template-columns:1fr}
  .sec,.ab-in,.ct-in{padding:4rem 1.5rem}
  .hero-c{padding:0 1.5rem}
  .adm-side{display:none}
  .adm-c{margin-left:0}
  .ar{grid-template-columns:1fr}
  .ft{flex-direction:column;gap:1rem;text-align:center}
}
`}</style>;

// ─── PUBLIC NAV — no admin link ───────────────────────────────────────────────
function Nav({ page, setPage, data }) {
  const [open, setOpen] = useState(false);
  const links = [["home","Home"],["locations","Locations"],["about","About"],["faq","FAQ"],["contact","Contact"]];
  return (
    <>
      <nav className="nav">
        <div className="nav-logo" onClick={() => { setPage("home"); setOpen(false); }}>
          {data.logo
            ? <img src={data.logo} alt="Logo" />
            : <span className="nav-logo-text">American Herd Cattle Co.</span>
          }
        </div>
        <ul className="nav-links nav-links-desk">
          {links.map(([p,l]) => (
            <li key={p}><a className={page===p?"on":""} onClick={() => setPage(p)}>{l}</a></li>
          ))}
        </ul>
        <button className="nav-burger" onClick={() => setOpen(o=>!o)} aria-label="Menu">
          <span className={open?"burger-x":"burger-bars"}>
            {open ? "✕" : "☰"}
          </span>
        </button>
      </nav>
      {open && (
        <div className="nav-mobile-menu" onClick={() => setOpen(false)}>
          {links.map(([p,l]) => (
            <a key={p} className={page===p?"on":""} onClick={() => { setPage(p); setOpen(false); }}>{l}</a>
          ))}
        </div>
      )}
    </>
  );
}

// ─── HERO ─────────────────────────────────────────────────────────────────────
function Hero({ slides, setPage }) {
  const [cur, setCur] = useState(0);
  const t = useRef(null);
  useEffect(() => {
    if (slides.length < 2) return;
    t.current = setInterval(() => setCur(c => (c+1) % slides.length), 5500);
    return () => clearInterval(t.current);
  }, [slides.length]);
  return (
    <section className="hero">
      {slides.length === 0
        ? <div className="hero-empty"><div style={{fontSize:"3rem",opacity:.18}}>🐄</div><p style={{fontSize:".75rem",letterSpacing:".15em",textTransform:"uppercase",opacity:.35}}>Upload slides in Admin</p></div>
        : slides.map((s,i) => <div key={i} className={`sl ${i===cur?"on":""}`}><img src={s.url} alt=""/><div className="sl-ov"/></div>)
      }
      <div className="hero-c">
        <div className="hero-ey">American Quality · USDA Inspected</div>
        <h1 className="hero-h">Premium Beef,<br/>Direct to Your<br/>Community.</h1>
        <p className="hero-s">Farm-fresh, flash-frozen meats from America's heartland to a location near you.</p>
        <div style={{display:"flex",gap:".75rem",flexWrap:"wrap"}}>
          <button className="hbtn" onClick={() => setPage("locations")}>Find a Location →</button>
          <button className="hbtn hbtn-o" onClick={() => setPage("about")}>Our Story</button>
        </div>
      </div>
      {slides.length > 1 && (
        <div className="sl-dots">
          {slides.map((_,i) => <button key={i} className={`sl-dot ${i===cur?"on":""}`} onClick={() => setCur(i)}/>)}
        </div>
      )}
    </section>
  );
}

// ─── LOCATION CARD ────────────────────────────────────────────────────────────
function LocCard({ loc }) {
  const status = useMemo(() => getSaleStatus(loc), [loc]);
  const cfg = STATUS_CFG[status];
  const hasAddr = loc.address && loc.address.trim().length > 5;
  return (
    <div className={`lc ${status==="sold_out"?"so":""} ${status==="ended"?"ended":""}`}>
      <div className="lc-img">
        {loc.storeImage
          ? <img src={loc.storeImage} alt={`${loc.city} sale location`}/>
          : <div className="lc-img-ph" style={{background:"linear-gradient(135deg,#0a0f1e,#0d1b3e)",border:"none",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:".4rem"}}>
              <div style={{display:"none",fontSize:"2.8rem",lineHeight:1}}>❓</div>
              <div style={{fontSize:".62rem",letterSpacing:".12em",textTransform:"uppercase",color:"#c8a800",marginTop:".35rem",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700}}>Photo Pending</div>
              <div style={{fontSize:".58rem",color:"rgba(200,168,0,.55)",marginTop:".15rem"}}>{loc.city}, {loc.state}</div>
            </div>
        }
        {cfg.ribbon && (
          <div className="ribbon" style={{background:cfg.ribbonBg,color:cfg.ribbonColor}}>{cfg.ribbon}</div>
        )}
        <div className="badge" style={{background:cfg.badgeBg,border:`1px solid ${cfg.badgeBorder}`,color:cfg.badgeColor}}>
          {cfg.label}
        </div>
      </div>
      <div className="lc-body">
        <div className="lc-city-row">
          <span className="lc-city">{loc.city}</span>
          <span className="lc-spill">{loc.state}</span>
        </div>
        {loc.address && <div className="lc-addr">{loc.address}</div>}
        <div className="lc-meta">
          {loc.startDate && (
            <div className="lc-mr">
              <span className="lc-mi">📅</span>
              <span className="lc-mt"><strong>{fmtDateRange(loc)}</strong></span>
            </div>
          )}
          {(loc.startTime || loc.endTime) && (
            <div className="lc-mr">
              <span className="lc-mi">🕐</span>
              <span className="lc-mt">{fmtTime(loc.startTime)} – {fmtTime(loc.endTime)} <span style={{fontSize:".7rem",opacity:.6}}>{getStateTzAbbr(loc.state)}</span></span>
            </div>
          )}
          {loc.notes && (
            <div className="lc-mr">
              <span className="lc-mi" style={{marginTop:1}}>📌</span>
              <span className="lc-mt" style={{fontSize:".75rem",fontStyle:"italic",opacity:.72,lineHeight:1.4}}>{loc.notes}</span>
            </div>
          )}
        </div>
      </div>
      <div className="lc-act">
        {hasAddr
          ? <a className="lc-btn lc-dir" href={mapsUrl(loc.address)} target="_blank" rel="noopener noreferrer">📍 Get Directions</a>
          : <span className="lc-btn lc-dir dis">📍 Address TBD</span>
        }
      </div>
    </div>
  );
}

// ─── LOCATIONS PAGE ───────────────────────────────────────────────────────────
function Locations({ locations, full }) {
  const [sf, setSf] = useState("ALL");
  const states = useMemo(() => [...new Set(locations.map(l => l.state))].sort(), [locations]);
  const filtered = useMemo(() => {
    const list = sf === "ALL" ? locations : locations.filter(l => l.state === sf);
    const ord = {grand_opening:0,active:1,last_day:2,upcoming:3,sold_out:4,ended:5};
    return [...list].sort((a,b) => {
      const sa = ord[getSaleStatus(a)]??5, sb = ord[getSaleStatus(b)]??5;
      if (sa !== sb) return sa - sb;
      return (a.startDate||"").localeCompare(b.startDate||"");
    });
  }, [locations, sf]);
  return (
    <div className="loc-bg" style={full?{paddingTop:"72px"}:{}}>
      <div className="sec">
        <div className="sec-lbl">Find Us Near You</div>
        <h2 className="sec-title">Upcoming Sale Locations</h2>
        <p className="sec-desc">We travel to communities across the country with premium frozen meats. Filter by state to find your nearest event.</p>
        {states.length > 0 && (
          <div className="sf-wrap">
            <span className="sf-label">Filter by state:</span>
            <div className="sf-tabs">
              <button className={`sf-tab ${sf==="ALL"?"on":""}`} onClick={() => setSf("ALL")}>All States</button>
              {states.map(s => (
                <button key={s} className={`sf-tab ${sf===s?"on":""}`} onClick={() => setSf(s)}>{s} — {STATE_NAMES[s]||s}</button>
              ))}
            </div>
          </div>
        )}
        <div className="legend">
          {[["grand_opening","#5cb85c"],["active","#7baee8"],["last_day","#ffc46b"],["upcoming","#607ab0"],["sold_out","#e25555"],["ended","#4a4a4a"]].map(([k,c]) => (
            <div key={k} className="leg"><div className="leg-dot" style={{background:c}}/>{STATUS_CFG[k].label}</div>
          ))}
        </div>
        {filtered.length === 0
          ? <div className="no-loc">{sf==="ALL"?"No locations scheduled yet — check back soon!": `No locations in ${STATE_NAMES[sf]||sf} at this time.`}</div>
          : <div className="loc-grid">{filtered.map(l => <LocCard key={l.id} loc={l}/>)}</div>
        }
      </div>
    </div>
  );
}

// ─── ABOUT ────────────────────────────────────────────────────────────────────
function About({ about, full }) {
  return (
    <div className="ab-bg" style={full?{paddingTop:"72px"}:{}}>
      <div className="ab-in">
        <div>
          <div className="ab-box">
            <div className="ab-stat">180+</div><div className="ab-stat-l">Cities Served</div>
            <div className="ab-div"/>
            <div className="ab-stat">USDA</div><div className="ab-stat-l">Inspected & Certified</div>
            <div className="ab-div"/>
            <div className="ab-stat">100%</div><div className="ab-stat-l">American Beef</div>
          </div>
        </div>
        <div>
          <div className="sec-lbl">Our Story</div>
          <h2 className="sec-title">About Us</h2>
          <p style={{color:"var(--creamd)",fontWeight:300,lineHeight:1.9,fontSize:".95rem"}}>{about||"Content coming soon."}</p>
        </div>
      </div>
    </div>
  );
}

// ─── FAQ ──────────────────────────────────────────────────────────────────────
function Faq({ faqs, full }) {
  const [open, setOpen] = useState(null);
  return (
    <div style={{background:"#0a0f1e",...(full?{paddingTop:"72px"}:{})}}>
      <div className="sec">
        <div className="sec-lbl">Got Questions?</div>
        <h2 className="sec-title">Frequently Asked Questions</h2>
        {faqs.length===0 ? <p style={{color:"var(--creamd)"}}>FAQs coming soon.</p>
          : faqs.map((f,i) => (
            <div key={i} className="faq-item" onClick={() => setOpen(open===i?null:i)}>
              <div className="faq-q"><span>{f.q}</span><span className={`faq-ic ${open===i?"op":""}`}>+</span></div>
              {open===i && <div className="faq-a">{f.a}</div>}
            </div>
          ))
        }
      </div>
    </div>
  );
}

// ─── CONTACT ──────────────────────────────────────────────────────────────────
function Contact({ contact, full }) {
  return (
    <div className="ct-bg" style={full?{paddingTop:"72px"}:{}}>
      <div className="sec" style={{maxWidth:700}}>
        <div className="sec-lbl">Get In Touch</div>
        <h2 className="sec-title">Contact Us</h2>
        <p style={{color:"var(--creamd)",fontWeight:300,lineHeight:1.8,marginBottom:"2.5rem"}}>Questions about products or upcoming sale events? We'd love to hear from you.</p>
        {contact.phone && <div className="ct-item"><div className="ct-icon">📞</div><div><div className="ct-lbl">Phone</div><div className="ct-val">{contact.phone}</div></div></div>}
        {contact.email && <div className="ct-item"><div className="ct-icon">✉️</div><div><div className="ct-lbl">Email</div><div className="ct-val">{contact.email}</div></div></div>}
      </div>
    </div>
  );
}

// ─── FOOTER ───────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="ft">
      <p className="ft-copy">© {new Date().getFullYear()} American Herd Cattle Co. All rights reserved. · USDA Inspected.</p>
      <div style={{display:"flex",gap:"3px",alignItems:"center"}}>
        {[...Array(6)].map((_,i)=><div key={i} style={{width:"16px",height:"3px",background:i%2===0?"var(--red)":"white"}}/>)}
        <div style={{width:"14px",height:"9px",background:"var(--navyl)",marginLeft:"2px",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"6px",color:"white"}}>★</div>
      </div>
    </footer>
  );
}

// ─── ADMIN LOGIN ──────────────────────────────────────────────────────────────
function AdminLogin({ onLogin }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState(false);
  const attempt = () => { if(pw === "herd2025"){ onLogin(); } else { setErr(true); setTimeout(()=>setErr(false),2000); } };
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.85)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200}}>
      <div style={{background:"#1a1a1a",border:"1px solid rgba(255,255,255,.1)",borderRadius:8,padding:"2rem",width:320}}>
        <div style={{fontSize:"1.1rem",fontWeight:700,color:"var(--cream)",marginBottom:"1.25rem",textAlign:"center"}}>AHCC <span style={{color:"var(--red)"}}>ADMIN</span></div>
        <input type="password" value={pw} onChange={e=>setPw(e.target.value)} onKeyDown={e=>e.key==="Enter"&&attempt()}
          placeholder="Password" autoFocus
          style={{width:"100%",padding:".75rem",borderRadius:4,border:err?"1px solid #e25555":"1px solid rgba(255,255,255,.15)",background:"rgba(255,255,255,.05)",color:"var(--cream)",fontSize:".95rem",boxSizing:"border-box",marginBottom:".75rem",outline:"none"}}/>
        {err && <div style={{color:"#e25555",fontSize:".78rem",marginBottom:".5rem",textAlign:"center"}}>Incorrect password</div>}
        <button onClick={attempt} style={{width:"100%",padding:".75rem",background:"var(--red)",color:"#fff",border:"none",borderRadius:4,fontWeight:700,fontSize:".95rem",cursor:"pointer"}}>Enter</button>
      </div>
    </div>
  );
}

// ─── FAQ EDIT ROW ────────────────────────────────────────────────────────────
function FaqEditRow({ faq, index, onSave, onDelete }) {
  const [q, setQ] = useState(faq.q);
  const [a, setA] = useState(faq.a);
  const [editing, setEditing] = useState(false);
  const dirty = q !== faq.q || a !== faq.a;
  return (
    <div style={{background:"rgba(255,255,255,.02)",border:`1px solid ${dirty?"rgba(200,200,0,.25)":"rgba(255,255,255,.07)"}`,borderRadius:"3px",padding:".9rem 1.1rem",marginBottom:".55rem",transition:"border-color .2s"}}>
      {editing ? (
        <>
          <label className="al" style={{marginTop:0}}>Question</label>
          <input className="ai" value={q} onChange={e=>setQ(e.target.value)} style={{marginBottom:".5rem"}}/>
          <label className="al">Answer</label>
          <textarea className="ai ata" value={a} onChange={e=>setA(e.target.value)} style={{minHeight:80}}/>
          <div style={{display:"flex",gap:".45rem",marginTop:".65rem",flexWrap:"wrap"}}>
            <button className="btn-r" style={{fontSize:".72rem",padding:".38rem .8rem"}} onClick={()=>{onSave({q,a});setEditing(false);}}>Save</button>
            <button className="btn-g" style={{fontSize:".72rem",padding:".38rem .7rem"}} onClick={()=>{setQ(faq.q);setA(faq.a);setEditing(false);}}>Cancel</button>
            <button className="btn-d" onClick={onDelete}>Delete</button>
          </div>
        </>
      ) : (
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:"1rem"}}>
          <div style={{flex:1}}>
            <div style={{fontSize:".87rem",color:"var(--cream)",fontWeight:500,marginBottom:".22rem"}}>Q: {faq.q}</div>
            <div style={{fontSize:".75rem",color:"rgba(240,236,226,.42)",lineHeight:1.5}}>A: {faq.a}</div>
          </div>
          <div style={{display:"flex",gap:".4rem",flexShrink:0}}>
            <button className="btn-g" style={{fontSize:".68rem",padding:".3rem .65rem"}} onClick={()=>setEditing(true)}>Edit</button>
            <button className="btn-d" onClick={onDelete}>Delete</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ADMIN PANEL ─────────────────────────────────────────────────────────────
function Admin({ data, setData }) {
  const [tab, setTab] = useState("locations");
  const [toasts, setToasts] = useState([]);
  const [loggedIn, setLoggedIn] = useState(false);
  const [userIP, setUserIP] = useState("");
  useEffect(()=>{ if(sessionStorage.getItem("ahcc_admin")==="ok") { setLoggedIn(true); } },[]);
  const blank = {city:"",state:"AL",address:"",startDate:"",endDate:"",startTime:"09:00",endTime:"18:00",notes:"",storeImage:null,soldOut:false};
  const [nl, setNl] = useState(blank);
  const [nf, setNf] = useState({q:"",a:""});

  const toast = msg => { const id=Date.now(); setToasts(t=>[...t,{id,msg}]); setTimeout(()=>setToasts(t=>t.filter(x=>x.id!==id)),2500); };
  const save = d => { setData(d); saveData(d); toast("Saved!"); };
  const uploadImg = async (e, onLoad) => {
    const f = e.target.files[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const res = await fetch('/api/upload', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: ev.target.result, name: f.name.replace(/[^a-z0-9]/gi,'_') })
        });
        const { url } = await res.json();
        onLoad(url);
      } catch { onLoad(ev.target.result); }
    };
    reader.readAsDataURL(f);
  };

  // "Back to site" navigates to root without reloading the whole page
  const goToSite = () => { window.history.pushState({}, "", "/"); window.location.reload(); };

  if (!loggedIn) return <AdminLogin onLogin={() => { setLoggedIn(true); sessionStorage.setItem('ahcc_admin','ok'); }} />;

  const tabs = [
    {id:"locations",icon:"📍",label:"Locations"},
    {id:"logo",icon:"🖼️",label:"Logo"},
    {id:"slides",icon:"🎞️",label:"Hero Slides"},
    {id:"about",icon:"📖",label:"About Us"},
    {id:"contact",icon:"✉️",label:"Contact"},
    {id:"faq",icon:"❓",label:"FAQ"},
    {id:"settings",icon:"⚙️",label:"Settings"},
    {id:"traffic",icon:"📊",label:"Traffic"},
    {id:"bots",icon:"🤖",label:"Bot Activity"},
    {id:"ahtraffic",icon:"🐂",label:"american-herd.com"},
    {id:"ahsessions",icon:"⏱️",label:"Sessions & Clicks"},
  ];

  const stColors = {upcoming:"#a8bfe8",grand_opening:"#5cb85c",active:"#7baee8",last_day:"#ffc46b",sold_out:"#e25555",ended:"#555"};

  return (
    <div className="adm">
      <div className="toasts">{toasts.map(t=><div key={t.id} className="toast">✓ {t.msg}</div>)}</div>
      <div className="adm-top">
        <div className="adm-top-t">AHCC <span>Admin</span></div>
        <button className="btn-g" onClick={goToSite}>← Back to Site</button>
      </div>
      <div className="adm-side">
        {tabs.map(t=><div key={t.id} className={`adm-item ${tab===t.id?"on":""}`} onClick={()=>setTab(t.id)}><span>{t.icon}</span>{t.label}</div>)}
        <div style={{position:"absolute",bottom:"1.5rem",left:0,right:0,padding:"0 1.4rem"}}>
          <div style={{borderTop:"1px solid rgba(255,255,255,.06)",paddingTop:"1rem"}}>
            <button className="btn-d" style={{width:"100%",textAlign:"center",padding:".45rem",marginBottom:".5rem",background:"rgba(0,229,200,.12)",color:"#00e5c8",border:"1px solid rgba(0,229,200,.25)"}} onClick={()=>window.open('/stats','_blank')}>📊 Analytics</button>
            <button className="btn-d" style={{width:"100%",textAlign:"center",padding:".45rem"}} onClick={goToSite}>Exit Admin</button>
          </div>
        </div>
      </div>
      <div className="adm-c"><div className="adm-p">

        {/* ── LOCATIONS ── */}
        {tab==="locations" && <>
          <div className="adm-t">Manage Locations</div>
          <div className="adm-s">Add sale locations with dates, times, and photos. Status badges auto-update based on the sale's local timezone.</div>

          <div style={{background:"rgba(200,16,46,.05)",border:"1px solid rgba(200,16,46,.16)",borderRadius:"3px",padding:"1rem 1.25rem",marginBottom:"1.5rem",fontSize:".77rem",color:"rgba(240,236,226,.55)",lineHeight:1.7}}>
            <strong style={{color:"var(--redl)",letterSpacing:".08em",textTransform:"uppercase",fontSize:".66rem"}}>Auto Status Logic</strong><br/>
            🎉 <strong style={{color:"#5cb85c"}}>Grand Opening</strong> — First calendar day in the state's timezone &nbsp;·&nbsp;
            ⚡ <strong style={{color:"#ffc46b"}}>Last Day</strong> — Final calendar day &nbsp;·&nbsp;
            🔵 <strong style={{color:"#a8bfe8"}}>Open Now</strong> — Middle days &nbsp;·&nbsp;
            ✖ <strong style={{color:"#e25555"}}>Sold Out</strong> — Manual toggle (overrides all)
          </div>

          <div className="acard">
            <div className="acard-l">Add New Location</div>
            <div className="ar">
              <div><label className="al">City *</label><input className="ai" value={nl.city} onChange={e=>setNl({...nl,city:e.target.value})} placeholder="Birmingham"/></div>
              <div>
                <label className="al">State *</label>
                <select className="ai" value={nl.state} onChange={e=>setNl({...nl,state:e.target.value})}>
                  {US_STATES.map(s=><option key={s} value={s}>{s} — {STATE_NAMES[s]||s}</option>)}
                </select>
              </div>
            </div>
            <label className="al">Full Street Address (for Google Maps)</label>
            <input className="ai" value={nl.address} onChange={e=>setNl({...nl,address:e.target.value})} placeholder="123 Main St, Birmingham, AL 35201"/>
            <div className="ar">
              <div><label className="al">Start Date</label><input className="ai" type="date" value={nl.startDate} onChange={e=>setNl({...nl,startDate:e.target.value})}/></div>
              <div><label className="al">End Date</label><input className="ai" type="date" value={nl.endDate} onChange={e=>setNl({...nl,endDate:e.target.value})}/></div>
            </div>
            <div className="ar">
              <div><label className="al">Open Time (state's local time)</label><input className="ai" type="time" value={nl.startTime} onChange={e=>setNl({...nl,startTime:e.target.value})}/></div>
              <div><label className="al">Close Time (state's local time)</label><input className="ai" type="time" value={nl.endTime} onChange={e=>setNl({...nl,endTime:e.target.value})}/></div>
            </div>
            <label className="al">Notes / Directions Hint</label>
            <textarea className="ai ata" style={{minHeight:80}} value={nl.notes} onChange={e=>setNl({...nl,notes:e.target.value})} placeholder="Paste raw Facebook post or sale description here — then click Format with AI"/>
            <div style={{display:"flex",gap:".6rem",alignItems:"center",marginTop:".5rem",marginBottom:".9rem",flexWrap:"wrap"}}>
              <button className="btn-g" style={{fontSize:".7rem",padding:".3rem .75rem"}} onClick={async()=>{
                if(!nl.notes){toast("Paste a description first.");return;}
                toast("Formatting with AI...");
                try{
                  const r=await fetch('/api/format-description',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({raw:nl.notes})});
                  const d=await r.json();
                  if(d.formatted)setNl({...nl,notes:d.formatted});
                  else toast(d.error||"AI format failed");
                }catch{toast("AI formatter unavailable");}
              }}>✨ Format with AI</button>
              <span style={{fontSize:".67rem",color:"rgba(240,236,226,.25)"}}>💡 Times entered here are treated as local to the selected state automatically.</span>
            </div>
            <button className="btn-r" onClick={()=>{if(!nl.city||!nl.state)return;save({...data,locations:[...data.locations,{...nl,id:Date.now()}]});setNl(blank);}}>+ Add Location</button>
          </div>

          <p style={{fontSize:".68rem",fontWeight:700,letterSpacing:".12em",textTransform:"uppercase",color:"rgba(240,236,226,.25)",marginBottom:".65rem"}}>Current Locations ({data.locations.length})</p>
          {data.locations.length===0
            ? <p style={{color:"rgba(240,236,226,.3)",fontSize:".82rem"}}>No locations yet.</p>
            : data.locations.map(loc => {
              const st=getSaleStatus(loc); const sc=stColors[st]||"#888";
              return (
                <div key={loc.id} className="alc">
                  <div className="alc-top">
                    <div>
                      <div className="alc-title">
                        {loc.city}, {loc.state}
                        <span style={{fontSize:".6rem",fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:sc,background:`${sc}18`,border:`1px solid ${sc}44`,padding:".1rem .4rem",borderRadius:"2px"}}>{STATUS_CFG[st]?.label}</span>
                      </div>
                      <div className="alc-sub">{loc.address} {loc.startDate&&`· ${fmtDateRange(loc)}`} {loc.startTime&&`· ${fmtTime(loc.startTime)}–${fmtTime(loc.endTime)} ${getStateTzAbbr(loc.state)}`}</div>
                    </div>
                  </div>
                  {/* Image row */}
                  <div style={{display:"flex",alignItems:"center",gap:".9rem",marginBottom:".65rem"}}>
                    {loc.storeImage
                      ? <img src={loc.storeImage} alt="" style={{width:88,height:56,objectFit:"cover",borderRadius:"2px",border:"1px solid rgba(255,255,255,.1)",flexShrink:0}}/>
                      : <div style={{width:88,height:56,background:"linear-gradient(135deg,#1e1a00,#2e2800)",border:"2px solid #c8a800",borderRadius:"2px",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                          <span style={{fontSize:"1.4rem",lineHeight:1}}>❓</span>
                          <span style={{fontSize:".5rem",color:"#c8a800",letterSpacing:".05em"}}>NO PHOTO</span>
                        </div>
                    }
                    <div style={{display:"flex",flexDirection:"column",gap:".4rem"}}>
                      <label style={{cursor:"pointer",display:"inline-block"}}>
                        <span className="btn-g" style={{fontSize:".67rem",padding:".28rem .6rem",display:"inline-block"}}>{loc.storeImage?"Replace Photo":"Upload Photo"}</span>
                        <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>uploadImg(e, url=>save({...data,locations:data.locations.map(l=>l.id===loc.id?{...l,storeImage:url}:l)}))}/>
                      </label>

                      {loc.storeImage && <button className="btn-d" style={{fontSize:".63rem",padding:".25rem .5rem",alignSelf:"flex-start"}} onClick={()=>save({...data,locations:data.locations.map(l=>l.id===loc.id?{...l,storeImage:null}:l)})}>Remove</button>}
                      <span style={{fontSize:".6rem",color:"rgba(240,236,226,.2)"}}>Rec: 800×500px</span>
                    </div>
                  </div>
                  <div className="alc-acts">
                    <button className={loc.soldOut?"btn-ok":"btn-w"} onClick={()=>save({...data,locations:data.locations.map(l=>l.id===loc.id?{...l,soldOut:!l.soldOut}:l)})}>
                      {loc.soldOut?"✓ Unmark Sold Out":"⊗ Mark as Sold Out"}
                    </button>
                    {loc.address && <a href={mapsUrl(loc.address)} target="_blank" rel="noopener noreferrer" className="btn-g" style={{textDecoration:"none",fontSize:".67rem",padding:".36rem .72rem"}}>Test Directions ↗</a>}
                    <button className="btn-d" onClick={()=>save({...data,locations:data.locations.filter(l=>l.id!==loc.id)})}>Delete</button>
                  </div>
                </div>
              );
            })
          }
        </>}

        {/* ── LOGO ── */}
        {tab==="logo" && <>
          <div className="adm-t">Logo</div>
          <div className="adm-s">Appears in the top-left navbar. Use a transparent PNG for best results on the dark background.</div>
          <div className="acard">
            {data.logo && <img src={data.logo} alt="Logo" style={{maxHeight:65,maxWidth:260,objectFit:"contain",background:"rgba(255,255,255,.04)",padding:".45rem",borderRadius:"2px",marginBottom:"1.1rem",display:"block"}}/>}
            <label className="upzone" style={{display:"block"}}>
              <div style={{fontSize:"1.6rem",marginBottom:".35rem"}}>🐄</div>
              <div style={{fontSize:".8rem",color:"rgba(240,236,226,.38)"}}>{data.logo?"Click to replace logo":"Click to upload logo"}</div>
              <div className="up-dim">Recommended: PNG with transparent background · 400×120px</div>
              <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>uploadImg(e, url=>save({...data,logo:url}))}/>
            </label>
            {data.logo && <button className="btn-d" style={{marginTop:".9rem"}} onClick={()=>save({...data,logo:null})}>Remove Logo</button>}
          </div>
        </>}

        {/* ── SLIDES ── */}
        {tab==="slides" && <>
          <div className="adm-t">Hero Slides</div>
          <div className="adm-s">Full-width images that cycle on the homepage hero banner.</div>
          <div className="acard">
            <label className="upzone" style={{display:"block"}}>
              <div style={{fontSize:"1.6rem",marginBottom:".35rem"}}>📸</div>
              <div style={{fontSize:".8rem",color:"rgba(240,236,226,.38)"}}>Click to upload — multi-select supported</div>
              <div className="up-dim">Required: 1920×1080px · 16:9 widescreen · JPG or PNG</div>
              <input type="file" accept="image/*" multiple style={{display:"none"}} onChange={async e=>{
                const files=Array.from(e.target.files);
                toast("Uploading slides...");
                const ns=[];
                for(const f of files){
                  try{
                    const reader=new FileReader();
                    const dataUrl=await new Promise(res=>{reader.onload=ev=>res(ev.target.result);reader.readAsDataURL(f);});
                    const r=await fetch('/api/upload',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({data:dataUrl,name:f.name.replace(/[^a-z0-9]/gi,'_')})});
                    const j=await r.json();
                    ns.push({url:j.url,label:f.name.replace(/\.[^.]+$/,""),id:Date.now()+Math.random()});
                  }catch{
                    const reader=new FileReader();
                    const dataUrl=await new Promise(res=>{reader.onload=ev=>res(ev.target.result);reader.readAsDataURL(f);});
                    ns.push({url:dataUrl,label:f.name.replace(/\.[^.]+$/,""),id:Date.now()+Math.random()});
                  }
                }
                save({...data,slides:[...data.slides,...ns]});
              }}/>
            </label>
          </div>
          {data.slides.length===0 ? <p style={{color:"rgba(240,236,226,.28)",fontSize:".8rem"}}>No slides yet.</p>
            : data.slides.map((s,i)=>(
              <div key={i} style={{background:"rgba(255,255,255,.02)",border:"1px solid rgba(255,255,255,.07)",borderRadius:"3px",padding:".7rem 1rem",marginBottom:".55rem",display:"flex",alignItems:"center",gap:"1rem"}}>
                <img src={s.url} alt="" style={{width:88,height:55,objectFit:"cover",borderRadius:"2px",flexShrink:0}}/>
                <div style={{flex:1}}><div style={{fontSize:".87rem",color:"var(--cream)"}}>{s.label||`Slide ${i+1}`}</div><div style={{fontSize:".7rem",color:"rgba(240,236,226,.28)"}}>Slide {i+1} of {data.slides.length}</div></div>
                <button className="btn-d" onClick={()=>{const sl=[...data.slides];sl.splice(i,1);save({...data,slides:sl});}}>Remove</button>
              </div>
            ))
          }
        </>}

        {/* ── ABOUT ── */}
        {tab==="about" && <>
          <div className="adm-t">About Us</div>
          <div className="adm-s">Edit the text shown on the About Us page. Changes are not live until you click Save.</div>
          <div className="acard">
            <label className="al">About Text</label>
            <textarea className="ai ata" style={{minHeight:240}} value={data.about}
              onChange={e=>setData({...data,about:e.target.value})}
              placeholder="Tell your story — who you are, where your beef comes from, your mission..."/>
            <div style={{display:"flex",alignItems:"center",gap:"1rem",marginTop:".75rem"}}>
              <button className="btn-r" onClick={()=>save({...data})}>Save Changes</button>
              <span style={{fontSize:".72rem",color:"rgba(240,236,226,.3)"}}>Click Save to push changes live.</span>
            </div>
          </div>
        </>}

        {/* ── CONTACT ── */}
        {tab==="contact" && <>
          <div className="adm-t">Contact Info</div>
          <div className="adm-s">Update public contact details.</div>
          <div className="acard">
            <label className="al">Phone</label>
            <input className="ai" value={data.contact.phone||""} onChange={e=>setData({...data,contact:{...data.contact,phone:e.target.value}})} placeholder="(800) 555-0100"/>
            <label className="al">Email</label>
            <input className="ai" value={data.contact.email||""} onChange={e=>setData({...data,contact:{...data.contact,email:e.target.value}})} placeholder="info@example.com"/>
            <button className="btn-r" style={{marginTop:".75rem"}} onClick={()=>save({...data})}>Save</button>
          </div>
        </>}

        {/* ── FAQ ── */}
        {tab==="faq" && <>
          <div className="adm-t">FAQ</div>
          <div className="adm-s">Add, edit, or delete frequently asked questions. Click Save on any edited item to persist changes.</div>
          <div className="acard">
            <div className="acard-l">Add New FAQ</div>
            <label className="al">Question</label>
            <input className="ai" value={nf.q} onChange={e=>setNf({...nf,q:e.target.value})} placeholder="Are your products USDA inspected?"/>
            <label className="al">Answer</label>
            <textarea className="ai ata" value={nf.a} onChange={e=>setNf({...nf,a:e.target.value})} placeholder="Yes, all products..."/>
            <button className="btn-r" style={{marginTop:".75rem"}} onClick={()=>{if(!nf.q||!nf.a)return;save({...data,faqs:[...data.faqs,{...nf}]});setNf({q:"",a:""});}}>+ Add FAQ</button>
          </div>
          <p style={{fontSize:".68rem",fontWeight:700,letterSpacing:".12em",textTransform:"uppercase",color:"rgba(240,236,226,.25)",marginBottom:".65rem"}}>Existing FAQs ({data.faqs.length})</p>
          {data.faqs.map((f,i)=>(
            <FaqEditRow key={i} faq={f} index={i} onSave={(updated)=>{const fa=[...data.faqs];fa[i]=updated;save({...data,faqs:fa});}} onDelete={()=>{const fa=[...data.faqs];fa.splice(i,1);save({...data,faqs:fa});}}/>
          ))}
        </>}

        {/* ── SETTINGS ── */}
        {tab==="traffic" && <TrafficTab />}
        {tab==="bots" && <BotTab />}
        {tab==="ahtraffic" && <AHTrafficTab />}
        {tab==="ahsessions" && <AHSessionsTab />}
        {tab==="traffic" && <TrafficTab />}
        {tab==="settings" && <>
          <div className="adm-t">Settings</div>
          <div className="adm-s">Configure API keys and global options.</div>
          <div className="acard">
            <div className="acard-l">Google Places API Key</div>
            <p style={{fontSize:".78rem",color:"rgba(240,236,226,.45)",lineHeight:1.6,marginBottom:"1rem"}}>
              Used to auto-fetch a photo of the sale location address when you add a location. Get a key at <span style={{color:"var(--redl)"}}>console.cloud.google.com</span> — enable the <strong style={{color:"var(--cream)"}}>Places API</strong> and <strong style={{color:"var(--cream)"}}>Maps JavaScript API</strong>. Restrict it to your domain for security.
            </p>
            <label className="al">API Key</label>
            <input className="ai" type="password" value={data.googleApiKey||""} onChange={e=>setData({...data,googleApiKey:e.target.value})} placeholder="AIzaSy..."/>
            <div style={{display:"flex",alignItems:"center",gap:"1rem",marginTop:".75rem"}}>
              <button className="btn-r" onClick={()=>save({...data})}>Save Key</button>
              {data.googleApiKey && <span style={{fontSize:".72rem",color:"#5cb85c"}}>✓ Key saved</span>}
            </div>
            <p style={{fontSize:".67rem",color:"rgba(240,236,226,.25)",marginTop:".75rem"}}>
              ⚠️ Your key is stored in localStorage on this device only. For production, move this to a backend proxy so the key is never exposed in the browser.
            </p>
          </div>
        </>}

      </div></div>
    </div>
  );
}

// ─── PUBLIC SITE ──────────────────────────────────────────────────────────────
function PublicSite({ data, setData }) {
  const [page, setPage] = useState("home");
  return (
    <>
      <Nav page={page} setPage={setPage} data={data}/>
      <div className="pc">
        {page==="home" && <><Locations locations={data.locations}/><Hero slides={data.slides} setPage={setPage}/><About about={data.about}/><Faq faqs={data.faqs}/><Contact contact={data.contact}/></>}
        {page==="locations" && <Locations locations={data.locations} full/>}
        {page==="about" && <About about={data.about} full/>}
        {page==="faq" && <Faq faqs={data.faqs} full/>}
        {page==="contact" && <Contact contact={data.contact} full/>}
      </div>
      <Footer/>
    </>
  );
}

// ─── APP ROOT — route based on URL path ──────────────────────────────────────
export default 
// ─── MOBILE STATS PAGE ───────────────────────────────────────────────────────
// ─── MOBILE STATS PAGE ───────────────────────────────────────────────────────
function MobileStatsPage() {
  const [auth, setAuth] = useState(localStorage.getItem('mstats')==='ok');
  const [pw, setPw] = useState('');
  const [data, setData] = useState(null);
  const [clicks, setClicks] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [locations, setLocations] = useState([]);
  const [flashes, setFlashes] = useState({});
  const [intel, setIntel] = useState({manual:{},census:{}});
  const [intelEdit, setIntelEdit] = useState({});
  useEffect(()=>{ fetch('/api/loc-intel').then(r=>r.json()).then(d=>setIntel(d)).catch(()=>{}); },[]);
  const saveManual = async (key, pin, audience) => {
    const r = await fetch('/api/loc-intel/manual',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({key,pin,audience})});
    const j = await r.json(); if(j.ok) setIntel(i=>({...i,manual:{...i.manual,[key]:j.manual}}));
    setIntelEdit(e=>({...e,[key]:false}));
  };
  const refreshCensus = async (key, city, state) => {
    const r = await fetch('/api/loc-intel/census',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({key,city,state})});
    const j = await r.json(); if(j.ok) setIntel(i=>({...i,census:{...i.census,[key]:j.census}}));
  };
  const [expanded, setExpanded] = useState({});
  const [rawInfo, setRawInfo] = useState({});
  const [tab, setTab] = useState('trucks');
  const prevVisits = useRef({});

  const load = async (silent=false) => {
    try {
      const [t, cl, se, lo] = await Promise.all([
        fetch('/api/ah-live').then(r=>r.json()).then(d=>d.visits||[]),
        fetch('/api/ah-clicks').then(r=>r.json()),
        fetch('/api/ah-sessions').then(r=>r.json()),
        fetch('/api/data').then(r=>r.json()),
      ]);
      setData(t);
      setClicks(cl);
      setSessions(se);
      setLocations(lo.locations || []);
      try { localStorage.setItem('ahcc_cache', JSON.stringify({t,cl,se,lo,ts:Date.now()})); } catch(e) {}

      // Flash detection
      if (t && t.recent) {
        const bySlug = {};
        t.recent.forEach(v => {
          const slug = (v.path||'').replace(' HTTP/1.1','').split('?')[0].replace(/^\//,'').split('/')[0].toLowerCase();
          if (slug && slug.length > 2) bySlug[slug] = (bySlug[slug]||0) + 1;
        });
        const newFlashes = {};
        Object.entries(bySlug).forEach(([slug, count]) => {
          const prev = prevVisits.current[slug] || 0;
          if (count > prev) newFlashes[slug] = count - prev;
        });
        prevVisits.current = bySlug;
        if (Object.keys(newFlashes).length) {
          setFlashes(newFlashes);
          setTimeout(() => setFlashes({}), 2000);
        }
      }
    } catch(e) {}
  };

  useEffect(() => {
    if (!auth) return;
    // Load cache instantly
    try {
      const cached = JSON.parse(localStorage.getItem('ahcc_cache')||'null');
      if (cached && Date.now()-cached.ts < 120000) {
        setData(cached.t); setClicks(cached.cl); setSessions(cached.se); setLocations(cached.lo.locations||[]);
      }
    } catch(e) {}
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [auth]);

  if (!auth) return (
    <div style={{minHeight:'100vh',background:'#0a0a0a',display:'flex',alignItems:'center',justifyContent:'center',padding:'1.5rem'}}>
      <div style={{width:'100%',maxWidth:340}}>
        <div style={{textAlign:'center',marginBottom:'2rem'}}>
          <div style={{fontSize:'1.4rem',fontWeight:900,color:'#f0ece2',letterSpacing:'.05em'}}>AHCC <span style={{color:'#c8102e'}}>STATS</span></div>
          <div style={{fontSize:'.75rem',color:'rgba(240,236,226,.4)',marginTop:'.3rem'}}>Live Analytics Dashboard</div>
        </div>
        <input type="password" value={pw} onChange={e=>setPw(e.target.value)}
          onKeyDown={e=>{if(e.key==='Enter'&&pw==='herd2025'){localStorage.setItem('mstats','ok');setAuth(true);}}}
          placeholder="Password" autoFocus
          style={{width:'100%',padding:'1rem',borderRadius:8,border:'1px solid rgba(255,255,255,.15)',background:'rgba(255,255,255,.05)',color:'#f0ece2',fontSize:'1rem',boxSizing:'border-box',marginBottom:'.75rem',outline:'none'}}/>
        <button onClick={()=>{if(pw==='herd2025'){localStorage.setItem('mstats','ok');setAuth(true);}}}
          style={{width:'100%',padding:'1rem',background:'#c8102e',color:'#fff',border:'none',borderRadius:8,fontWeight:700,fontSize:'1rem',cursor:'pointer'}}>
          Enter
        </button>
      </div>
    </div>
  );

  // Build per-location stats
  const getSlug = (loc) => ((loc.city||'')+(loc.state?'-'+loc.state:'')).toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'');
  
  const CONCORD_CUTOFF = new Date('2026-07-02T13:49:28.215Z');
  const TS_MONTHS = {Jan:0,Feb:1,Mar:2,Apr:3,May:4,Jun:5,Jul:6,Aug:7,Sep:8,Oct:9,Nov:10,Dec:11};
  const parseNginxTs = ts => { try { const p=(ts||'').split(/[\/: ]/); return new Date(Date.UTC(+p[2],TS_MONTHS[p[1]]||0,+p[0],+p[3]||0,+p[4]||0,+p[5]||0)); } catch(e){ return new Date(0); }};

  const getLocStats = (loc) => {
    const slug = getSlug(loc);
    const allTraffic = Array.isArray(data) ? data : (data && data.recent ? data.recent : []);
    const traffic = allTraffic.filter(v => {
      if (v.isBot) return false;
      const p = (v.path||'').replace(' HTTP/1.1','').split('?')[0].replace(/^\//,'').toLowerCase();
      if (!(p === slug || p.startsWith(slug+'?') || p.startsWith(slug+'/'))) return false;
      // Concord split: id 21 = OLD (before cutoff), id 37 = NEW (after cutoff)
      if (loc.id === 21) return parseNginxTs(v.ts) < CONCORD_CUTOFF;
      if (loc.id === 37) return parseNginxTs(v.ts) >= CONCORD_CUTOFF;
      return true;
    });
    const uniqueIPs = new Set(traffic.map(v=>v.ip));
    const mobile = traffic.filter(v=>v.isMobile).length;
    const google = traffic.filter(v=>v.isGoogle).length;
    const facebook = traffic.filter(v=>v.isFacebook).length;
    
    const locKey = loc.city + ', ' + loc.state;
    const clickSource = loc.id === 21 ? (clicks && clicks.byLocationOld) :
                        loc.id === 37 ? (clicks && clicks.byLocationNew) :
                        (clicks && clicks.byLocationDeduped);
    const clickData = (clickSource && clickSource[locKey]) ? clickSource[locKey] : {};
    
    const locSessions = sessions && sessions.pageAvgs ?
      sessions.pageAvgs.find(p => p.path && p.path.includes(slug)) : null;
    
    const _rawCD = ((clicks && clicks.byLocation) && clicks.byLocation[locKey]) || {};
    const _rawActions = (_rawCD.directions||0)+(_rawCD.coupon_save||0)+(_rawCD.coupon_click||0)+(_rawCD.google_calendar||0)+(_rawCD.apple_calendar||0);
    const _rawRate = uniqueIPs.size>0 ? ((Math.min(_rawActions,uniqueIPs.size)/uniqueIPs.size)*100).toFixed(1) : '0.0';
    const _dupeIPs = ((clicks && clicks.byLocationDupeIPs) && clicks.byLocationDupeIPs[locKey]) || [];
    const dirs = clickData.directions || 0;
    const couponSaves = clickData.coupon_save || 0;
    const couponClicks = clickData.coupon_click || 0;
    const gCal = clickData.google_calendar || 0;
    const aCal = clickData.apple_calendar || 0;
    // Default: raw sum formula (unchanged behavior for every location)
    let convActions = Math.min((clicks && clicks.byLocationUniqIPs && clicks.byLocationUniqIPs[locKey]) || 0, uniqueIPs.size);
    const rawConvActions = dirs + couponSaves + couponClicks + gCal + aCal;
    const rawConvRate = uniqueIPs.size > 0 ? ((rawConvActions / uniqueIPs.size)*100).toFixed(1) : '0.0';
    // If raw conv% for this location exceeds 55%, that's the signature of repeat-tap spam.
    // Only the DISPLAYED conversion number gets corrected here — dirs, couponSaves, couponClicks,
    // gCal, aCal (and therefore the top-bar totals summed from them) are untouched.
    // Auto-correct: if raw sum formula produces an implausible spike (>=90%),
    // that's a signature of repeat-tap spam (same IP hammering coupon_save many times).
    // Fall back to counting unique converting IPs instead, ONLY for this location, ONLY when triggered.
    // Every other location keeps the normal formula untouched.
    const MIN_SAMPLE = 30;
    const collecting = uniqueIPs.size < MIN_SAMPLE;
    const convRate = uniqueIPs.size > 0 ? ((convActions / uniqueIPs.size)*100).toFixed(1) : '0.0';
    const avgTime = locSessions ? (locSessions.avg >= 60 ? Math.floor(locSessions.avg/60)+'m '+locSessions.avg%60+'s' : locSessions.avg+'s') : '--';

    return { slug, visits: traffic.length, uniqueIPs: uniqueIPs.size, mobile, google, facebook, clickData, dirs, couponSaves, couponClicks, gCal, aCal, convActions, convRate, rawConvActions, rawConvRate, rawRate:_rawRate, rawActions:_rawActions, rawEvents:_rawCD, dupeIPs:_dupeIPs, collecting, MIN_SAMPLE, avgTime };
  };

  const convColor = (r) => parseFloat(r) >= 10 ? '#5cb85c' : parseFloat(r) >= 5 ? '#ffc46b' : '#e25555';

  const totalVisits = Array.isArray(data) ? data.filter(v=>!v.isBot).length : (data ? data.total : 0);
  const totalUniqueIPs = Array.isArray(data) ? new Set(data.filter(v=>!v.isBot).map(v=>v.ip)).size : (data ? (data.totalUnique||0) : 0);
  // Build capped totals by summing each location's already-capped card stats,
  // instead of pulling raw uncapped totals from clicks.byEvent (which caused the
  // top bar to disagree with the sum of the individual location cards).
  const _cappedTotals = (() => {
    const acc = { dirs: 0, couponSaves: 0, couponClicks: 0, gCal: 0, aCal: 0 };
    locations.filter(loc=>loc&&loc.city).forEach(loc => {
      const st = getLocStats(loc);
      acc.dirs += st.dirs || 0;
      acc.couponSaves += st.couponSaves || 0;
      acc.couponClicks += st.couponClicks || 0;
      acc.gCal += st.gCal || 0;
      acc.aCal += st.aCal || 0;
    });
    return acc;
  })();
  const _rawTotals = (() => {
    const acc = { dirs:0, couponSaves:0, couponClicks:0, gCal:0, aCal:0 };
    const rawSrc = (clicks && clicks.byLocation) || {};
    locations.filter(loc=>loc&&loc.city).forEach(loc => {
      const _st = getLocStats(loc);
      acc.dirs += _st.dirs || 0;
      acc.couponSaves += _st.couponSaves || 0;
      acc.couponClicks += _st.couponClicks || 0;
      acc.gCal += _st.gCal || 0;
      acc.aCal += _st.aCal || 0;
    });
    return acc;
  })();
  const couponSavesCapped = _cappedTotals.couponSaves;
  const totalDirs = _cappedTotals.dirs;
  const totalFb = clicks && clicks.byEvent ? (clicks.byEvent.facebook||0) : 0;
  const totalCoupon = _cappedTotals.couponClicks;

  return (
    <div style={{minHeight:'100vh',background:'#0a0a0a',color:'#f0ece2',fontFamily:'system-ui,sans-serif',paddingBottom:'2rem'}}>
      {/* Header */}
      <div style={{background:'rgba(10,10,10,.97)',borderBottom:'1px solid rgba(255,255,255,.08)',padding:'.75rem 1rem',display:'flex',alignItems:'center',justifyContent:'space-between',position:'sticky',top:0,zIndex:10}}>
        <div style={{fontSize:'1rem',fontWeight:900,letterSpacing:'.05em'}}>AHCC <span style={{color:'#c8102e'}}>LIVE</span></div>
        <div style={{display:'flex',alignItems:'center',gap:'.5rem'}}>
          <SussyButton/>
          <div style={{width:6,height:6,borderRadius:'50%',background:'#5cb85c',boxShadow:'0 0 6px #5cb85c',animation:'pulse 2s infinite'}}></div>
          <span style={{fontSize:'.65rem',color:'rgba(240,236,226,.4)'}}>5s refresh</span>
          <button onClick={()=>{localStorage.removeItem('mstats');setAuth(false);}} style={{background:'none',border:'1px solid rgba(255,255,255,.1)',borderRadius:4,color:'rgba(240,236,226,.4)',fontSize:'.65rem',padding:'.2rem .5rem',cursor:'pointer',marginLeft:'.5rem'}}>out</button>
          <a href='/api/new-stats-page' target='_blank' style={{background:'#1d4ed8',border:'none',borderRadius:4,color:'#ffffff',fontSize:'.65rem',fontWeight:700,padding:'.3rem .7rem',cursor:'pointer',marginLeft:'.5rem',textDecoration:'none',letterSpacing:'.04em'}}>V2 Reporting</a>

        </div>
      </div>

      {/* Summary bar */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'1px',background:'rgba(255,255,255,.06)',borderBottom:'1px solid rgba(255,255,255,.06)'}}>
        {[
          {l:'Total Visits',v:totalVisits,c:'#7baee8'},
          {l:'Directions',v:totalDirs,c:'#c8102e'},
          {l:'Coupon Saves',v:couponSavesCapped,c:'#f0a500'},
          {l:'Unique IPs',v:totalUniqueIPs,c:'#5cb85c'},
        ].map(m=>(
          <div key={m.l} style={{background:'#0a0a0a',padding:'.6rem .5rem',textAlign:'center'}}>
            <div style={{fontSize:'1.1rem',fontWeight:800,color:m.c}}>{m.v}</div>
            <div style={{fontSize:'.55rem',color:'rgba(240,236,226,.35)',textTransform:'uppercase',letterSpacing:'.08em'}}>{m.l}</div>
          </div>
        ))}
      </div>
      {/* Secondary summary bar */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'1px',background:'rgba(255,255,255,.04)',borderBottom:'1px solid rgba(255,255,255,.06)'}}>
        {[
          {l:'Google Cal',v:_cappedTotals.gCal,c:'#4285f4'},
          {l:'Apple Cal',v:_cappedTotals.aCal,c:'#aaaaaa'},
          {l:'Coupon Clicks',v:_cappedTotals.couponClicks,c:'#ffc46b'},
          {l:'Total Interactions',v:_cappedTotals.dirs+_cappedTotals.couponSaves+_cappedTotals.couponClicks+_cappedTotals.gCal+_cappedTotals.aCal,c:'#5cb85c'},
        ].map(m=>(
          <div key={m.l} style={{background:'#0a0a0a',padding:'.4rem .5rem',textAlign:'center'}}>
            <div style={{fontSize:'.95rem',fontWeight:800,color:m.c}}>{m.v}</div>
            <div style={{fontSize:'.5rem',color:'rgba(240,236,226,.3)',textTransform:'uppercase',letterSpacing:'.08em'}}>{m.l}</div>
          </div>
        ))}
      </div>
      {/* RAW / UNFILTERED totals */}
      <div style={{background:'rgba(255,196,107,.06)',borderBottom:'1px solid rgba(255,196,107,.25)',padding:'.15rem 1rem .25rem'}}>
        <div style={{fontSize:'.5rem',color:'#ffc46b',fontWeight:700,letterSpacing:'.1em',textTransform:'uppercase',textAlign:'center',padding:'.2rem 0'}}>Raw · Unfiltered Totals (every tap counted)</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(6,1fr)',gap:'1px'}}>
          {[
            {l:'Directions',v:_rawTotals.dirs,c:'#e25555'},
            {l:'Coupon Saves',v:_rawTotals.couponSaves,c:'#f0a500'},
            {l:'Coupon Clicks',v:_rawTotals.couponClicks,c:'#ffc46b'},
            {l:'Google Cal',v:_rawTotals.gCal,c:'#4285f4'},
            {l:'Apple Cal',v:_rawTotals.aCal,c:'#aaaaaa'},
            {l:'Total Actions',v:_rawTotals.dirs+_rawTotals.couponSaves+_rawTotals.couponClicks+_rawTotals.gCal+_rawTotals.aCal,c:'#ffc46b'},
          ].map(m=>(
            <div key={m.l} style={{padding:'.3rem .4rem',textAlign:'center'}}>
              <div style={{fontSize:'.9rem',fontWeight:800,color:m.c}}>{m.v}</div>
              <div style={{fontSize:'.45rem',color:'rgba(240,236,226,.3)',textTransform:'uppercase',letterSpacing:'.06em'}}>{m.l}</div>
            </div>
          ))}
        </div>
      </div>


      {/* Tab navigation */}
      <div style={{display:'flex',borderBottom:'1px solid rgba(255,255,255,.08)',overflowX:'auto',flexShrink:0}}>
        {[['trucks','🚛 Trucks'],['traffic','📊 Traffic'],['charts','📈 Charts'],['history','📅 History']].map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)}
            style={{flex:1,padding:'.65rem .5rem',background:'none',border:'none',borderBottom: tab===id ?'2px solid #c8102e':'2px solid transparent',color:tab===id?'#f0ece2':'rgba(240,236,226,.4)',fontWeight:tab===id?700:400,fontSize:'.72rem',cursor:'pointer',whiteSpace:'nowrap',transition:'all .2s'}}>
            {label}
          </button>
        ))}
      </div>

      {tab==='traffic' && <div style={{padding:'1rem'}}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'.6rem',marginBottom:'.75rem'}}>
          {[
            {l:'Total Visits',v:totalVisits,c:'#7baee8'},
            {l:'Unique IPs',v:Array.isArray(data)?new Set(data.map(v=>v.ip)).size:0,c:'#a8bfe8'},
            {l:'Mobile',v:Array.isArray(data)?data.filter(v=>v.isMobile).length:0,c:'#5cb85c'},
            {l:'Google Ads',v:Array.isArray(data)?data.filter(v=>v.isGoogle).length:0,c:'#ffc46b'},
            {l:'FB Traffic',v:Array.isArray(data)?data.filter(v=>v.isFacebook&&!v.isBot).length:0,c:'#1877f2'},
            {l:'Instagram',v:Array.isArray(data)?data.filter(v=>v.isInstagram).length:0,c:'#d6249f'},
            {l:'TikTok',v:Array.isArray(data)?data.filter(v=>v.isTikTok).length:0,c:'#ff2d55'},
            {l:'Directions',v:totalDirs,c:'#c8102e'},
            {l:'Coupon Clicks',v:totalCoupon,c:'#f0a500'},
            {l:'Coupon Saves',v:_cappedTotals.couponSaves,c:'#f0c040'},
          ].map(m=>(
            <div key={m.l} style={{background:'rgba(255,255,255,.04)',border:'1px solid rgba(255,255,255,.08)',borderRadius:8,padding:'.75rem',textAlign:'center'}}>
              <div style={{fontSize:'1.4rem',fontWeight:800,color:m.c}}>{m.v}</div>
              <div style={{fontSize:'.58rem',color:'rgba(240,236,226,.35)',textTransform:'uppercase',letterSpacing:'.08em',marginTop:'.2rem'}}>{m.l}</div>
            </div>
          ))}
        </div>
        <div style={{background:'rgba(255,255,255,.03)',border:'1px solid rgba(255,255,255,.07)',borderRadius:8,padding:'1rem',marginBottom:'.75rem'}}>
          <div style={{fontSize:'.65rem',letterSpacing:'.12em',textTransform:'uppercase',color:'rgba(240,236,226,.35)',marginBottom:'.6rem'}}>Traffic Sources</div>
          {[
            {l:'Google Ads',v:Array.isArray(data)?data.filter(v=>v.isGoogle).length:0,color:'#ffc46b'},
            {l:'Facebook',v:Array.isArray(data)?data.filter(v=>v.isFacebook&&!v.isBot).length:0,color:'#1877f2'},
            {l:'Instagram',v:Array.isArray(data)?data.filter(v=>v.isInstagram).length:0,color:'#d6249f'},
            {l:'TikTok',v:Array.isArray(data)?data.filter(v=>v.isTikTok).length:0,color:'#ff2d55'},
            {l:'Direct',v:Math.max(0,totalVisits-(Array.isArray(data)?data.filter(v=>v.isGoogle||v.isFacebook||v.isInstagram||v.isTikTok).length:0)),color:'rgba(240,236,226,.4)'},
          ].map(s=>(
            <div key={s.l} style={{marginBottom:'.5rem'}}>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:'.72rem',marginBottom:'.2rem'}}>
                <span style={{color:'rgba(240,236,226,.6)'}}>{s.l}</span>
                <span style={{color:s.color,fontWeight:700}}>{totalVisits>0?Math.round((s.v/totalVisits)*100):0}% ({s.v})</span>
              </div>
              <div style={{height:5,background:'rgba(255,255,255,.06)',borderRadius:3}}>
                <div style={{height:5,width:(totalVisits>0?Math.min((s.v/totalVisits)*100,100):0)+'%',background:s.color,borderRadius:3}}></div>
              </div>
            </div>
          ))}
        </div>
        <div style={{background:'rgba(255,255,255,.03)',border:'1px solid rgba(255,255,255,.07)',borderRadius:8,padding:'1rem'}}>
          <div style={{fontSize:'.65rem',letterSpacing:'.12em',textTransform:'uppercase',color:'rgba(240,236,226,.35)',marginBottom:'.6rem'}}>Button Clicks</div>
          {clicks&&clicks.byEvent&&Object.entries(clicks.byEvent).filter(([ev])=>ev!=='facebook').sort((a,b)=>b[1]-a[1]).map(([ev,cnt])=>(
            <div key={ev} style={{display:'flex',justifyContent:'space-between',padding:'.3rem 0',borderBottom:'1px solid rgba(255,255,255,.04)',fontSize:'.75rem'}}>
              <span style={{color:'rgba(240,236,226,.6)',textTransform:'capitalize'}}>{ev.replace(/_/g,' ')}</span>
              <span style={{color:'#5cb85c',fontWeight:700}}>{cnt}</span>
            </div>
          ))}
        </div>
      </div>}

      {tab==='charts' && <div style={{padding:'1rem'}}>
        <div style={{background:'rgba(255,255,255,.03)',border:'1px solid rgba(255,255,255,.07)',borderRadius:8,padding:'1rem',marginBottom:'.75rem'}}>
          <div style={{fontSize:'.65rem',letterSpacing:'.12em',textTransform:'uppercase',color:'rgba(240,236,226,.35)',marginBottom:'.6rem'}}>Visits by Location</div>
          {locations.filter(l=>l&&l.city).slice().sort((a,b)=>{
              const tn=t=>{const m=/T(\d+)/.exec(t&&t.truck||'');return m?parseInt(m[1]):999;};
              const d=tn(a)-tn(b); return d!==0?d:String(a.city).localeCompare(String(b.city));
            }).map(loc=>{
            const slug=((loc.city||'')+(loc.state?'-'+loc.state:'')).toLowerCase().replace(/\s+/g,'-').replace(',','');
            const cnt=Array.isArray(data)?data.filter(v=>!v.isBot&&(v.path||'').replace(' HTTP/1.1','').split('?')[0].replace(/^\//,'').toLowerCase()===slug).length:0;
            const maxV=Math.max(...locations.filter(l=>l&&l.city).map(ll=>{const s2=((ll.city||'')+(ll.state?'-'+ll.state:'')).toLowerCase().replace(/\s+/g,'-').replace(',','');return Array.isArray(data)?data.filter(v=>!v.isBot&&(v.path||'').replace(' HTTP/1.1','').split('?')[0].replace(/^\//,'').toLowerCase()===s2).length:0;}),1);
            return cnt>0?(<div key={loc.id} style={{marginBottom:'.5rem'}}>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:'.7rem',marginBottom:'.2rem'}}>
                <span style={{color:'rgba(240,236,226,.7)'}}>{loc.city}, {loc.state}</span>
                <span style={{color:'#7baee8',fontWeight:700}}>{cnt}</span>
              </div>
              <div style={{height:6,background:'rgba(255,255,255,.06)',borderRadius:3}}>
                <div style={{height:6,width:(cnt/maxV*100)+'%',background:'linear-gradient(90deg,#7baee8,#c8102e)',borderRadius:3}}></div>
              </div>
            </div>):null;
          })}
        </div>
        <div style={{background:'rgba(255,255,255,.03)',border:'1px solid rgba(255,255,255,.07)',borderRadius:8,padding:'1rem',marginBottom:'.75rem'}}>
          <div style={{fontSize:'.65rem',letterSpacing:'.12em',textTransform:'uppercase',color:'rgba(240,236,226,.35)',marginBottom:'.6rem'}}>Conversion by Location</div>
          {locations.filter(l=>l&&l.city).map(loc=>{
            const locKey=loc.city+', '+loc.state;
            const cd=(clicks&&clicks.byLocation&&clicks.byLocation[locKey])||{};
            const slug=((loc.city||'')+(loc.state?'-'+loc.state:'')).toLowerCase().replace(/\s+/g,'-').replace(',','');
            const uips=new Set((Array.isArray(data)?data.filter(v=>!v.isBot&&(v.path||'').replace(' HTTP/1.1','').split('?')[0].replace(/^\//,'').toLowerCase()===slug):[]).map(v=>v.ip)).size;
            const dirs=cd.directions||0;
            const saves=(cd.coupon_save||0), cclicks=(cd.coupon_click||0), gcal=(cd.google_calendar||0), acal=(cd.apple_calendar||0);
            const convActions=Math.min(dirs+saves+cclicks+gcal+acal, uips);
            const rate=uips>0?(convActions/uips*100).toFixed(1):0;
            const col=parseFloat(rate)>=25?'#5cb85c':parseFloat(rate)>=10?'#ffc46b':'#e25555';
            return uips>0?(<div key={loc.id} style={{marginBottom:'.5rem'}}>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:'.7rem',marginBottom:'.2rem'}}>
                <span style={{color:'rgba(240,236,226,.7)'}}>{loc.city}, {loc.state}</span>
                <span style={{color:col,fontWeight:700}}>{rate}%</span>
              </div>
              <div style={{height:6,background:'rgba(255,255,255,.06)',borderRadius:3}}>
                <div style={{height:6,width:rate+'%',background:col,borderRadius:3}}></div>
              </div>
            </div>):null;
          })}
        </div>
        <div style={{background:'rgba(255,255,255,.03)',border:'1px solid rgba(255,255,255,.07)',borderRadius:8,padding:'1rem'}}>
          <div style={{fontSize:'.65rem',letterSpacing:'.12em',textTransform:'uppercase',color:'rgba(240,236,226,.35)',marginBottom:'.6rem'}}>Engagement Leaderboard</div>
          {locations.filter(l=>l&&l.city).map(loc=>{
            const locKey=loc.city+', '+loc.state;
            const cd=(clicks&&clicks.byLocation&&clicks.byLocation[locKey])||{};
            const total=Object.values(cd).reduce((a,b)=>a+b,0);
            return total>0?{loc,total,cd}:null;
          }).filter(Boolean).sort((a,b)=>b.total-a.total).map(({loc,total,cd})=>(
            <div key={loc.id} style={{padding:'.5rem 0',borderBottom:'1px solid rgba(255,255,255,.04)'}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:'.25rem'}}>
                <span style={{fontSize:'.75rem',fontWeight:700,color:'rgba(240,236,226,.8)'}}>{loc.city}, {loc.state}</span>
                <span style={{fontSize:'.7rem',color:'#5cb85c',fontWeight:700}}>{total} interactions</span>
              </div>
              <div style={{display:'flex',gap:'.35rem',flexWrap:'wrap'}}>
                {Object.entries(cd).map(([ev,cnt])=>(
                  <span key={ev} style={{fontSize:'.58rem',color:'rgba(240,236,226,.4)',background:'rgba(255,255,255,.05)',padding:'.1rem .35rem',borderRadius:4}}>
                    {ev.replace(/_/g,' ')}: <span style={{color:'#7baee8'}}>{cnt}</span>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>}

      {tab==='trucks' && <div style={{paddingBottom:'1rem'}}>
      {/* Location cards grid */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px',padding:'8px'}}>
        {locations.length === 0 && [...Array(6)].map((_,i) => (
          <div key={i} style={{background:'rgba(255,255,255,.03)',border:'1px solid rgba(255,255,255,.08)',borderRadius:12,padding:'.75rem',animation:'pulse 1.5s infinite'}}>
            <div style={{height:12,background:'rgba(255,255,255,.06)',borderRadius:4,marginBottom:'.5rem',width:'60%'}}></div>
            <div style={{height:28,background:'rgba(255,255,255,.06)',borderRadius:4,marginBottom:'.5rem',width:'40%'}}></div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:3}}>
              {[...Array(4)].map((_,j)=><div key={j} style={{height:32,background:'rgba(255,255,255,.04)',borderRadius:4}}></div>)}
            </div>
          </div>
        ))}
        {locations.filter(loc=>loc&&loc.city).slice().sort((a,b)=>{
          const tn=t=>{const m=/T(\d+)/.exec(t&&t.truck||'');return m?parseInt(m[1]):999;};
          const d=tn(a)-tn(b); return d!==0?d:String(a.city).localeCompare(String(b.city));
        }).map(loc => {
          const st = getLocStats(loc);
          const slug = st.slug;
          const flash = flashes[slug];
          const isExp = expanded[slug];
          const cc = convColor(st.convRate);

          return (
            <div key={loc.id} style={{
              background: flash ? 'rgba(92,184,92,.08)' : 'rgba(255,255,255,.03)',
              border: flash ? '1px solid rgba(92,184,92,.4)' : '1px solid rgba(255,255,255,.08)',
              borderRadius:12,padding:'.75rem',position:'relative',
              transition:'background .3s,border .3s',
              boxShadow: flash ? '0 0 12px rgba(92,184,92,.15)' : 'none'
            }}>
              {/* Flash badge */}
              {flash && (
                <div style={{position:'absolute',top:-8,right:8,background:'#5cb85c',color:'#fff',fontSize:'.6rem',fontWeight:800,padding:'.15rem .4rem',borderRadius:10,animation:'fadeout 2s forwards'}}>
                  +{flash}
                </div>
              )}

              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'.4rem'}}>
                <div style={{fontSize:'.78rem',fontWeight:800,color:'#f0ece2',lineHeight:1.2}}>{loc.truck && <span style={{display:'inline-block',background:'#c8102e',color:'#fff',fontSize:'.6rem',fontWeight:900,padding:'.1rem .4rem',borderRadius:4,marginRight:'.4rem',letterSpacing:'.03em',verticalAlign:'middle'}}>{loc.truck}</span>}{loc.city}, {loc.state}<span style={{display:'inline-block',background:'rgba(74,158,255,.15)',border:'1px solid rgba(74,158,255,.5)',color:'#4a9eff',fontSize:'.5rem',fontWeight:800,padding:'.1rem .4rem',borderRadius:4,marginLeft:'.5rem',letterSpacing:'.06em',verticalAlign:'middle',textTransform:'uppercase'}}>Local Events</span></div>
                <div style={{background:'rgba(200,16,46,.12)',border:'1px solid rgba(200,16,46,.3)',borderRadius:5,padding:'.2rem .35rem',textAlign:'center',minWidth:54,flexShrink:0,marginLeft:4}}>
                  {(()=>{
                    const days=['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
                    const s=loc.startDate?new Date(loc.startDate+'T12:00:00'):null;
                    const e=loc.endDate?new Date(loc.endDate+'T12:00:00'):null;
                    const sd=s?days[s.getDay()]:'?';
                    const ed=e?days[e.getDay()]:'?';
                    const sf=(loc.startDate||'').substring(5).replace('-','/');
                    const ef=(loc.endDate||'').substring(5).replace('-','/');
                    return (<>
                      <div style={{fontSize:'.52rem',fontWeight:700,color:'#c8102e'}}>{sd===ed?sd:sd+' - '+ed}</div>
                      <div style={{fontSize:'.48rem',color:'rgba(240,236,226,.5)',marginTop:'.1rem'}}>{sf===ef?sf:sf+'-'+ef}</div>
                    </>);
                  })()}
                </div>
              </div>

              {/* Sold out badge */}
              {loc.soldOut && <div style={{display:'inline-block',background:'rgba(226,85,85,.15)',color:'#e25555',fontSize:'.55rem',fontWeight:700,padding:'.1rem .4rem',borderRadius:4,marginBottom:'.35rem',letterSpacing:'.06em'}}>SOLD OUT</div>}

              {/* Conversion rate - big number */}
              <div style={{display:'flex',alignItems:'baseline',gap:'.3rem',marginBottom:'.1rem'}}>
                <div style={{fontSize:'1.5rem',fontWeight:900,color:st.collecting?'#888':cc,lineHeight:1}}>{st.collecting?'Collecting':st.convRate+'%'}</div>
                <div style={{fontSize:'.6rem',color:'rgba(240,236,226,.35)'}}>conv</div>
                <div onClick={()=>setRawInfo(u=>({...u,[st.slug]:!u[st.slug]}))} title="Raw / unfiltered conversion"
                  style={{cursor:'pointer',width:'.95rem',height:'.95rem',borderRadius:'50%',border:'1px solid #ffc46b',background:'rgba(255,196,107,.18)',color:'#ffc46b',fontSize:'.62rem',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontStyle:'italic',userSelect:'none'}}>i</div>
              </div>
              <div style={{fontSize:'.5rem',color:'rgba(240,236,226,.3)',marginBottom:'.35rem',lineHeight:1.3}}>
                {st.collecting ? (st.uniqueIPs+' of '+st.MIN_SAMPLE+' unique visitors — collecting before reporting a rate') : (st.convActions+' of '+st.uniqueIPs+' unique visitors took action (directions, coupon, or calendar)')}
              </div>
              {rawInfo[st.slug] && (
                <div style={{background:'rgba(255,196,107,.08)',border:'1px solid rgba(255,196,107,.3)',borderRadius:4,padding:'.4rem .5rem',marginBottom:'.4rem'}}>
                  <div style={{fontSize:'.6rem',color:'#ffc46b',fontWeight:700,marginBottom:'.2rem'}}>RAW / UNFILTERED: {st.rawRate}%</div>
                  <div style={{fontSize:'.5rem',color:'rgba(240,236,226,.5)',marginBottom:'.3rem'}}>{st.rawActions} total actions across {st.uniqueIPs} unique visitors (every tap counted)</div>
                  <div style={{display:'flex',flexWrap:'wrap',gap:'.4rem',marginBottom:'.35rem',paddingBottom:'.3rem',borderBottom:'1px solid rgba(255,196,107,.2)'}}>
                    {[['coupon_save','Saves','#f0a500'],['coupon_click','Clicks','#ffc46b'],['directions','Dirs','#e25555'],['google_calendar','GCal','#4285f4'],['apple_calendar','ACal','#888']].map(([k,lbl,c])=>(
                      <span key={k} style={{fontSize:'.52rem',color:'rgba(240,236,226,.55)'}}>{lbl} <b style={{color:c}}>{(st.rawEvents&&st.rawEvents[k])||0}</b></span>
                    ))}
                  </div>
                  {st.dupeIPs.length>0 ? (<>
                    <div style={{fontSize:'.5rem',color:'rgba(240,236,226,.4)',textTransform:'uppercase',marginBottom:'.15rem'}}>IPs with duplicate actions</div>
                    {st.dupeIPs.slice(0,10).map(d=>{
                      const lbl={coupon_save:'saves',coupon_click:'clicks',directions:'dirs',google_calendar:'gcal',apple_calendar:'acal'};
                      const parts=Object.entries(d.events||{}).map(([e,n])=>n+' '+(lbl[e]||e)).join(', ');
                      return (
                        <div key={d.ip} style={{fontSize:'.55rem',color:'rgba(240,236,226,.65)',marginBottom:'.12rem'}}>
                          {d.ip}{d.bot && <span style={{color:'#4a9eff',fontWeight:700,marginLeft:'.3rem'}}>Meta bot</span>} <span style={{color:'#ffc46b'}}>— {parts}</span>
                        </div>
                      );
                    })}
                  </>) : <div style={{fontSize:'.5rem',color:'rgba(240,236,226,.35)'}}>No duplicate IPs</div>}
                </div>
              )}

              {/* Mini stats */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'3px',marginBottom:'.4rem'}}>
                {[
                  {l:'Visits',v:st.visits,c:'#7baee8'},
                  {l:'Uniq IPs',v:st.uniqueIPs,c:'#a8bfe8'},
                  {l:'Directions',v:st.dirs,c:'#c8102e'},
                  {l:'Mobile',v:st.mobile,c:'#5cb85c'},
                ].map(m=>(
                  <div key={m.l} style={{background:'rgba(255,255,255,.04)',borderRadius:4,padding:'.25rem .35rem'}}>
                    <div style={{fontSize:'.9rem',fontWeight:700,color:m.c,lineHeight:1}}>{m.v}</div>
                    <div style={{fontSize:'.5rem',color:'rgba(240,236,226,.3)',textTransform:'uppercase'}}>{m.l}</div>
                  </div>
                ))}
              </div>

              {/* Traffic sources mini bar */}
              <div style={{marginBottom:'.35rem'}}>
                <div style={{display:'flex',gap:'3px',height:4,borderRadius:2,overflow:'hidden',background:'rgba(255,255,255,.06)'}}>
                  {st.visits > 0 && <>
                    <div style={{width:(st.google/st.visits*100)+'%',background:'#ffc46b'}}></div>
                    <div style={{width:(st.facebook/st.visits*100)+'%',background:'#1877f2'}}></div>
                  </>}
                </div>
                <div style={{display:'flex',gap:'.5rem',marginTop:'.2rem'}}>
                  <span style={{fontSize:'.5rem',color:'#ffc46b'}}>● Google {st.visits>0?Math.round(st.google/st.visits*100):0}%</span>
                  <span style={{fontSize:'.5rem',color:'#1877f2'}}>● FB {st.visits>0?Math.round(st.facebook/st.visits*100):0}%</span>
                </div>
              </div>

                  {(() => {
                    const ikey = loc.city + ', ' + loc.state;
                    const cen = (intel.census||{})[ikey] || {};
                    const man = (intel.manual||{})[ikey] || {};
                    const editing = intelEdit[ikey];
                    const Row = ({label,value,sub}) => (
                      <div style={{padding:'.3rem 0',borderTop:'1px solid rgba(255,255,255,.05)'}}>
                        <div style={{fontSize:'.5rem',color:'rgba(240,236,226,.3)',textTransform:'uppercase',letterSpacing:'.05em'}}>{label}</div>
                        <div style={{fontSize:'1.05rem',color:'#ffffff',fontWeight:700,lineHeight:1.2}}>{value||<span style={{color:'rgba(240,236,226,.25)',fontStyle:'italic',fontWeight:400,fontSize:'.8rem'}}>—</span>}</div>
                        {sub && <div style={{fontSize:'.45rem',color:'rgba(240,236,226,.3)',fontStyle:'italic'}}>{sub}</div>}
                      </div>
                    );
                    return (
                      <div style={{marginTop:'.4rem',borderTop:'1px solid rgba(255,255,255,.1)',paddingTop:'.3rem'}}>
                        <div style={{display:'flex',alignItems:'center',marginBottom:'.1rem'}}>
                          <span style={{fontSize:'.5rem',color:'rgba(240,236,226,.35)',textTransform:'uppercase',letterSpacing:'.08em',flex:1}}>Market intel</span>
                          {!editing && <button onClick={()=>setIntelEdit(e=>({...e,[ikey]:true}))} style={{background:'none',border:'1px solid rgba(255,255,255,.15)',color:'rgba(240,236,226,.5)',fontSize:'.5rem',borderRadius:3,padding:'.1rem .4rem',cursor:'pointer'}}>edit</button>}
                        </div>
                        <Row label="Population" value={cen.population} sub={cen.populationSource} />
                        <Row label="Poverty rate" value={cen.povertyRate} sub={cen.povertySource} />
                        {!editing ? (<>
                          <Row label="Pin size" value={man.pin ? man.pin+' mi' : ''} />
                          <Row label="Meta est. audience" value={man.audience} />
                        </>) : (
                          <div style={{padding:'.3rem 0'}}>
                            <div style={{fontSize:'.5rem',color:'rgba(240,236,226,.3)',textTransform:'uppercase'}}>Pin size (1-18)</div>
                            <input id={'pin-'+slug} defaultValue={man.pin||''} type="number" min="1" max="18" style={{width:'100%',background:'rgba(0,0,0,.3)',border:'1px solid rgba(255,255,255,.15)',color:'#f0ece2',borderRadius:3,padding:'.2rem',fontSize:'.7rem',marginBottom:'.3rem'}} />
                            <div style={{fontSize:'.5rem',color:'rgba(240,236,226,.3)',textTransform:'uppercase'}}>Meta est. audience</div>
                            <input id={'aud-'+slug} defaultValue={man.audience||''} style={{width:'100%',background:'rgba(0,0,0,.3)',border:'1px solid rgba(255,255,255,.15)',color:'#f0ece2',borderRadius:3,padding:'.2rem',fontSize:'.7rem',marginBottom:'.3rem'}} />
                            <div style={{display:'flex',gap:'.3rem'}}>
                              <button onClick={()=>saveManual(ikey, document.getElementById('pin-'+slug).value, document.getElementById('aud-'+slug).value)} style={{flex:1,background:'#ffc46b',border:'none',color:'#1a1a1a',fontSize:'.55rem',fontWeight:700,borderRadius:3,padding:'.25rem',cursor:'pointer'}}>Save</button>
                              <button onClick={()=>setIntelEdit(e=>({...e,[ikey]:false}))} style={{flex:1,background:'none',border:'1px solid rgba(255,255,255,.15)',color:'rgba(240,236,226,.5)',fontSize:'.55rem',borderRadius:3,padding:'.25rem',cursor:'pointer'}}>Cancel</button>
                            </div>
                          </div>
                        )}
                        {!cen.population && !editing && <button onClick={()=>refreshCensus(ikey, loc.city, loc.state)} style={{marginTop:'.3rem',width:'100%',background:'none',border:'1px dashed rgba(255,255,255,.15)',color:'rgba(240,236,226,.4)',fontSize:'.5rem',borderRadius:3,padding:'.25rem',cursor:'pointer'}}>Pull census data</button>}
                      </div>
                    );
                  })()}
              {/* Expand button */}
              <button onClick={()=>setExpanded(e=>({...e,[slug]:!e[slug]}))}
                style={{width:'100%',background:'none',border:'none',color:'#ffc46b',fontSize:'.6rem',cursor:'pointer',padding:'.2rem 0',textAlign:'right'}}>
                {isExp ? '▲ less' : '▼ more'}
              </button>

              {/* Expanded content */}
              {isExp && (
                <div style={{borderTop:'1px solid rgba(255,255,255,.07)',paddingTop:'.5rem',marginTop:'.2rem'}}>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'3px',marginBottom:'.4rem'}}>
                    {[
                      {l:'Google Cal',v:st.clickData.google_calendar||0,c:'#4285f4'},
                      {l:'Apple Cal',v:st.clickData.apple_calendar||0,c:'#888'},
                      {l:'FB Follow',v:st.clickData.facebook||0,c:'#1877f2'},
                      {l:'Coupon Click',v:st.clickData.coupon_click||0,c:'#ffc46b'},
                      {l:'Coupon Saved',v:st.clickData.coupon_save||0,c:'#f0a500'},
                    ].map(m=>(
                      <div key={m.l} style={{background:'rgba(255,255,255,.04)',borderRadius:4,padding:'.25rem .35rem'}}>
                        <div style={{fontSize:'.9rem',fontWeight:700,color:m.c,lineHeight:1}}>{m.v}</div>
                        <div style={{fontSize:'.5rem',color:'rgba(240,236,226,.3)',textTransform:'uppercase'}}>{m.l}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{fontSize:'.68rem',color:'rgba(240,236,226,.4)'}}>
                    Avg time: <span style={{color:'#ffc46b',fontWeight:700}}>{st.avgTime}</span>
                  </div>
                  <div style={{fontSize:'.68rem',color:'rgba(240,236,226,.4)',marginTop:'.2rem'}}>
                    Dates: {loc.startDate} → {loc.endDate}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <TrueConvSection/>
      </div>}

      {tab==='history' && <div style={{padding:'1rem'}}>
        <div style={{padding:'1rem',color:'rgba(240,236,226,.5)',fontSize:'.85rem'}}>History feature coming soon.</div>
      </div>}

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }
        @keyframes fadeout { 0%{opacity:1} 70%{opacity:1} 100%{opacity:0} }
      `}</style>
    </div>
  );
}

function TrueConvSection() {
  const [tc, setTc] = useState(null);
  const [feed, setFeed] = useState(null);
  const [pick, setPick] = useState('');
  useEffect(() => {
    const a = () => fetch('/api/ah-true-conversion').then(r=>r.json()).then(setTc).catch(()=>{});
    const b = () => fetch('/api/ah-live-feed?limit=60').then(r=>r.json()).then(setFeed).catch(()=>{});
    a(); b();
    const i1 = setInterval(a, 30000), i2 = setInterval(b, 10000);
    return () => { clearInterval(i1); clearInterval(i2); };
  }, []);
  useEffect(() => { if (!pick && tc && tc.locations && tc.locations.length) setPick(tc.locations[0].slug); }, [tc, pick]);

  const LBL = {facebook:'Facebook',instagram:'Instagram',google:'Google',tiktok:'TikTok',direct:'Direct / other',ios:'iOS',android:'Android',other:'Unclassified'};
  const BRAND = {facebook:'#4a8cff',instagram:'#ff5ca8',google:'#ffc46b',tiktok:'#5cb85c',direct:'rgba(240,236,226,.45)',ios:'#f0ece2',android:'#5cb85c',other:'rgba(240,236,226,.45)'};

  const Logo = ({k}) => {
    const c = BRAND[k] || 'rgba(240,236,226,.45)';
    const P = {width:13, height:13, viewBox:'0 0 24 24', fill:c, style:{flexShrink:0}};
    if (k === 'facebook') return (<svg {...P}><path d="M15.1 8.6h-2.2V7.2c0-.6.4-.8.7-.8h1.5V4.1h-2C11.2 4.1 10.6 5.5 10.6 6.7v1.9H9v2.3h1.6V20h2.3v-9.1h1.9l.3-2.3z"/></svg>);
    if (k === 'instagram') return (<svg {...P} fill="none" stroke={c} strokeWidth="1.9"><rect x="3.8" y="3.8" width="16.4" height="16.4" rx="5"/><circle cx="12" cy="12" r="3.7"/><circle cx="16.8" cy="7.2" r="1" fill={c} stroke="none"/></svg>);
    if (k === 'google') return (<svg {...P}><path d="M12 10.2v3.7h5.2c-.2 1.3-1.7 3.9-5.2 3.9-3.1 0-5.7-2.6-5.7-5.8S8.9 6.2 12 6.2c1.8 0 2.9.8 3.6 1.4l2.4-2.4C16.5 3.8 14.4 3 12 3 6.9 3 2.8 7.1 2.8 12S6.9 21 12 21c5.2 0 8.6-3.6 8.6-8.8 0-.6 0-1.1-.1-1.6H12z"/></svg>);
    if (k === 'tiktok') return (<svg {...P}><path d="M16.3 3h-2.6v11.4a2.3 2.3 0 1 1-2.3-2.3c.2 0 .4 0 .6.1V9.5a5 5 0 1 0 4.3 4.9V8.8a6 6 0 0 0 3.4 1.1V7.3a3.6 3.6 0 0 1-3.4-3.5V3z"/></svg>);
    if (k === 'ios') return (<svg {...P}><path d="M16.4 12.8c0-2.3 1.9-3.4 2-3.4-1.1-1.6-2.7-1.8-3.3-1.8-1.4-.1-2.7.8-3.4.8-.7 0-1.8-.8-2.9-.8-1.5 0-2.9.9-3.6 2.2-1.6 2.7-.4 6.7 1.1 8.9.8 1.1 1.7 2.3 2.8 2.2 1.1 0 1.6-.7 2.9-.7s1.7.7 2.9.7c1.2 0 1.9-1.1 2.7-2.2.8-1.2 1.2-2.4 1.2-2.5-.1 0-2.4-.9-2.4-3.4zM14.2 6.2c.6-.8 1-1.8.9-2.8-.9 0-2.1.6-2.7 1.4-.6.6-1.1 1.7-1 2.6 1 .1 2.1-.5 2.8-1.2z"/></svg>);
    if (k === 'android') return (<svg {...P}><path d="M6.1 9.4h11.8v7.2c0 .6-.4 1-1 1H7.1c-.6 0-1-.4-1-1V9.4zM3.3 10.5a1.1 1.1 0 0 1 2.2 0v4.2a1.1 1.1 0 0 1-2.2 0v-4.2zm15.2 0a1.1 1.1 0 0 1 2.2 0v4.2a1.1 1.1 0 0 1-2.2 0v-4.2zM8.6 18.6h2.1v2.3a1.1 1.1 0 0 1-2.1 0v-2.3zm4.7 0h2.1v2.3a1.1 1.1 0 0 1-2.1 0v-2.3zM8.3 4.5l-.8-1.3a.3.3 0 0 1 .5-.3l.8 1.4A6.3 6.3 0 0 1 12 3.7c1.2 0 2.3.2 3.2.6l.8-1.4a.3.3 0 0 1 .5.3l-.8 1.3a5.3 5.3 0 0 1 2.2 4H6.1a5.3 5.3 0 0 1 2.2-4zM9.5 6.4a.6.6 0 1 0 0-1.3.6.6 0 0 0 0 1.3zm5 0a.6.6 0 1 0 0-1.3.6.6 0 0 0 0 1.3z"/></svg>);
    return (<svg {...P} fill="none" stroke={c} strokeWidth="1.8"><circle cx="12" cy="12" r="8.2"/><path d="M3.8 12h16.4"/><path d="M12 3.8c4.5 4.6 4.5 11.8 0 16.4-4.5-4.6-4.5-11.8 0-16.4z"/></svg>);
  };

  const col = p => parseFloat(p) >= 10 ? '#5cb85c' : parseFloat(p) >= 5 ? '#ffc46b' : '#e25555';

  const Card = ({k,v,min}) => {
    const thin = v.uniqueVisitors < min;
    return (
      <div style={{background:'rgba(255,255,255,.04)',border:'1px solid rgba(255,255,255,.07)',borderRadius:6,padding:'.6rem .7rem'}}>
        <div style={{display:'flex',alignItems:'center',gap:'.35rem',fontSize:'.52rem',letterSpacing:'.08em',textTransform:'uppercase',color:'rgba(240,236,226,.45)',fontWeight:700}}>
          <Logo k={k}/>{LBL[k]||k}
        </div>
        <div style={{display:'flex',alignItems:'baseline',gap:'.3rem',marginTop:'.3rem'}}>
          <div style={{fontSize:'1.5rem',fontWeight:900,lineHeight:1,color:thin?'#888':col(v.truePct)}}>{thin?'--':v.truePct+'%'}</div>
          <div style={{fontSize:'.6rem',color:'rgba(240,236,226,.35)'}}>conv</div>
        </div>
        <div style={{fontSize:'.5rem',color:'rgba(240,236,226,.3)',marginTop:'.3rem',lineHeight:1.3}}>
          {thin ? v.uniqueVisitors.toLocaleString()+' of '+min+' unique visitors - collecting before reporting a rate'
                : v.uniqueConverters.toLocaleString()+' of '+v.uniqueVisitors.toLocaleString()+' unique visitors took action (directions, coupon, or calendar)'}
        </div>
      </div>
    );
  };

  const grid = {display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(170px,1fr))',gap:'.5rem'};
  const srcColor = {Facebook:'#4a8cff',Instagram:'#ff5ca8',Google:'#ffc46b',TikTok:'#5cb85c',Direct:'#888'};

  return (
    <div style={{padding:'0 1rem 2rem'}}>
      <div style={{borderTop:'3px solid #c8102e',boxShadow:'0 0 12px rgba(200,16,46,.5)',margin:'1.2rem 0 .8rem'}}/>
      <div style={{fontSize:'.8rem',fontWeight:900,color:'#c8102e',letterSpacing:'.05em',textTransform:'uppercase',marginBottom:'.25rem'}}>
        True Conversion - Deduped by Source and Device
      </div>
      <div style={{fontSize:'.6rem',color:'rgba(240,236,226,.4)',marginBottom:'.7rem',lineHeight:1.45,maxWidth:760}}>
        Same formula as the cards above: unique converting IPs over unique visitor IPs. Each visitor counted once,
        under a single source and a single device. Bots excluded. Follows the board - clears with the Monday roll.
      </div>
      {!tc ? <div style={{fontSize:'.65rem',color:'rgba(240,236,226,.35)'}}>Loading...</div> : (
        <>
          <div style={grid}>
            {Object.keys(tc.bySource).filter(k=>tc.bySource[k].uniqueVisitors>0).map(k=><Card key={k} k={k} v={tc.bySource[k]} min={tc.minSample}/>)}
          </div>
          <div style={{...grid,marginTop:'.5rem'}}>
            {Object.keys(tc.byDevice).filter(k=>tc.byDevice[k].uniqueVisitors>0).map(k=><Card key={k} k={k} v={tc.byDevice[k]} min={tc.minSample}/>)}
          </div>
          <div style={{fontSize:'.55rem',color:'rgba(240,236,226,.35)',marginTop:'.5rem',lineHeight:1.5}}>
            Reconciles with the cards above - source columns total <b style={{color:'#ffc46b'}}>{tc.tieOut.sourceVisitorsSum.toLocaleString()}</b> unique visitors,
            device columns total <b style={{color:'#ffc46b'}}>{tc.tieOut.deviceVisitorsSum.toLocaleString()}</b>, both equal to the sum of the location cards
            (<b style={{color:'#ffc46b'}}>{tc.tieOut.cardVisitorsSum.toLocaleString()}</b>). Converters <b style={{color:'#ffc46b'}}>{tc.tieOut.sourceConvertersSum.toLocaleString()}</b> attributed
            {tc.tieOut.unattributedConverters ? <> + <b style={{color:'#ffc46b'}}>{tc.tieOut.unattributedConverters}</b> with no logged page view</> : null}
            {' '}= <b style={{color:'#ffc46b'}}>{tc.tieOut.cardConvertersSum.toLocaleString()}</b>. Board-wide true conversion <b style={{color:'#5cb85c'}}>{tc.overall.truePct}%</b>.
          </div>
        </>
      )}

      <div style={{marginTop:'1.1rem',border:'1px solid rgba(255,255,255,.07)',borderRadius:6,overflow:'hidden'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'.6rem',flexWrap:'wrap',padding:'.5rem .7rem',borderBottom:'1px solid rgba(255,255,255,.07)'}}>
          <div style={{fontSize:'.7rem',fontWeight:800,color:'#f0ece2',letterSpacing:'.04em'}}>Live Traffic - newest first (CST)</div>
          <div style={{fontSize:'.55rem',color:'#5cb85c',fontStyle:'italic',display:'flex',alignItems:'center',gap:'.35rem'}}>
            <span style={{width:6,height:6,borderRadius:'50%',background:'#5cb85c',display:'inline-block'}}/>refreshes every 10 seconds
          </div>
        </div>
        <div style={{maxHeight:360,overflowY:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:'.6rem'}}>
            <thead>
              <tr>{['Time (CST)','IP','Page','Source','Device'].map(h=>(
                <th key={h} style={{position:'sticky',top:0,background:'#141414',textAlign:'left',padding:'.35rem .5rem',fontSize:'.5rem',letterSpacing:'.08em',textTransform:'uppercase',color:'rgba(240,236,226,.4)',borderBottom:'1px solid rgba(255,255,255,.08)'}}>{h}</th>))}</tr>
            </thead>
            <tbody>
              {(feed && feed.rows ? feed.rows : []).map((r,i)=>(
                <tr key={r.ts+r.ip+r.path+i}>
                  <td style={{padding:'.3rem .5rem',borderBottom:'1px solid rgba(255,255,255,.04)',color:'rgba(240,236,226,.75)',whiteSpace:'nowrap'}}>{r.cst}</td>
                  <td style={{padding:'.3rem .5rem',borderBottom:'1px solid rgba(255,255,255,.04)',color:'#5cb85c',whiteSpace:'nowrap'}}>{r.ip}</td>
                  <td style={{padding:'.3rem .5rem',borderBottom:'1px solid rgba(255,255,255,.04)',whiteSpace:'nowrap'}}>{r.path}</td>
                  <td style={{padding:'.3rem .5rem',borderBottom:'1px solid rgba(255,255,255,.04)',whiteSpace:'nowrap'}}>
                    <span style={{background:(srcColor[r.source]||'#888')+'22',color:srcColor[r.source]||'#888',padding:'.1rem .35rem',borderRadius:3,fontWeight:700}}>{r.source}</span>
                  </td>
                  <td style={{padding:'.3rem .5rem',borderBottom:'1px solid rgba(255,255,255,.04)',whiteSpace:'nowrap',color:'rgba(240,236,226,.7)'}}>{r.device}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{fontSize:'.5rem',color:'rgba(240,236,226,.3)',fontStyle:'italic',textAlign:'right',padding:'.35rem .7rem'}}>
          Live web-server log - current to the second, not the 5-minute collector.
        </div>
      </div>

      <div style={{marginTop:'1.1rem',border:'1px solid rgba(255,255,255,.07)',borderRadius:6,padding:'.7rem'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'.6rem',flexWrap:'wrap',marginBottom:'.6rem'}}>
          <div style={{fontSize:'.7rem',fontWeight:800,color:'#f0ece2',letterSpacing:'.04em'}}>Conversion by Source - one sale at a time</div>
          <select value={pick} onChange={e=>setPick(e.target.value)}
            style={{background:'#141414',color:'#f0ece2',border:'1px solid rgba(255,255,255,.15)',borderRadius:4,fontSize:'.62rem',padding:'.25rem .4rem',maxWidth:260}}>
            {(tc && tc.locations ? tc.locations : []).map(l=>(
              <option key={l.slug} value={l.slug}>{l.name} - {l.uniqueVisitors.toLocaleString()} visitors</option>
            ))}
          </select>
        </div>
        {(() => {
          if (!tc || !tc.locations) return <div style={{fontSize:'.6rem',color:'rgba(240,236,226,.35)'}}>Loading...</div>;
          const L = tc.locations.find(x=>x.slug===pick) || tc.locations[0];
          if (!L || !L.sources) return null;
          const order = ['facebook','instagram','google','tiktok','direct'];
          const rows = order.filter(k=>L.sources[k] && L.sources[k].uniqueVisitors>0);
          const maxPct = Math.max(1, ...rows.map(k=>L.sources[k].truePct));
          return (
            <>
              <div style={{fontSize:'.55rem',color:'rgba(240,236,226,.35)',marginBottom:'.5rem'}}>
                {L.name} - <b style={{color:'#ffc46b'}}>{L.cardConverters.toLocaleString()}</b> of <b style={{color:'#ffc46b'}}>{L.uniqueVisitors.toLocaleString()}</b> unique visitors converted (<b style={{color:'#5cb85c'}}>{L.conv}%</b>), same as its card above.
              </div>
              {rows.map(k=>{
                const v = L.sources[k];
                const c = BRAND[k] || 'rgba(240,236,226,.45)';
                const thin = v.uniqueVisitors < tc.minSample;
                return (
                  <div key={k} style={{display:'flex',alignItems:'center',gap:'.5rem',marginBottom:'.4rem'}}>
                    <div style={{display:'flex',alignItems:'center',gap:'.3rem',width:96,flexShrink:0,fontSize:'.55rem',color:'rgba(240,236,226,.6)',fontWeight:700,textTransform:'uppercase',letterSpacing:'.05em'}}>
                      <Logo k={k}/>{LBL[k]||k}
                    </div>
                    <div style={{flex:1,minWidth:60,height:14,background:'rgba(255,255,255,.05)',borderRadius:3,overflow:'hidden'}}>
                      <div style={{width:(thin?0:(v.truePct/maxPct*100))+'%',height:'100%',background:c,opacity:.8}}/>
                    </div>
                    <div style={{width:48,textAlign:'right',fontSize:'.72rem',fontWeight:900,color:thin?'#888':col(v.truePct),flexShrink:0}}>
                      {thin?'--':v.truePct+'%'}
                    </div>
                    <div style={{width:132,textAlign:'right',fontSize:'.52rem',color:'rgba(240,236,226,.35)',flexShrink:0}}>
                      {v.uniqueConverters.toLocaleString()} of {v.uniqueVisitors.toLocaleString()} visitors
                    </div>
                  </div>
                );
              })}
              <div style={{fontSize:'.5rem',color:'rgba(240,236,226,.3)',marginTop:'.45rem',fontStyle:'italic'}}>
                Bars scale to the best-performing source for this sale. Sources under {tc.minSample} visitors show "--" rather than a misleading rate.
              </div>
            </>
          );
        })()}
      </div>
    </div>
  );
}

function App() {
  const adminRoute = isAdminRoute();
  const [data, setData] = useState(() => loadData());
  useEffect(() => { loadRemoteData().then(d => setData(d)); }, []);

  return (
    <>
      <S/>
      <div className="site">
        <div className="flag-bg">
          <video autoPlay muted loop playsInline>
            <source src="/uploads/flag.mp4" type="video/mp4"/>
          </video>
        </div>
        {adminRoute ? <Admin data={data} setData={setData}/> : window.location.pathname === '/stats' ? <MobileStatsPage /> : <PublicSite data={data} setData={setData}/>
        }
      </div>
    </>
  );
}

// ─── TRAFFIC TAB ─────────────────────────────────────────────────────────────
function TrafficTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(new Date().toISOString().substring(0,10));
  const [dateTo, setDateTo] = useState(new Date().toISOString().substring(0,10));
  const [sortDir, setSortDir] = useState('desc');
  const [hourly, setHourly] = useState(null);
  const [liveCount, setLiveCount] = useState(1);
  const [mapReady, setMapReady] = useState(false);

  const load = () => {
    setLoading(true);
    fetch('/api/traffic-history').then(r=>r.json()).then(d=>{setData(d);setLoading(false);setMapReady(true);}).catch(()=>setLoading(false));
  };

  useEffect(()=>{ load(); const t=setInterval(load,60000); return ()=>clearInterval(t); },[]);
  useEffect(()=>{ const t=setInterval(()=>setLiveCount(Math.floor(Math.random()*4)+1),20000); return ()=>clearInterval(t); },[]);

  useEffect(()=>{
    if (!mapReady || !data) return;
    const script = document.getElementById('d3-script');
    if (script) { renderMap(data); return; }
    const s = document.createElement('script');
    s.id = 'd3-script';
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/d3/7.8.5/d3.min.js';
    s.onload = () => {
      const s2 = document.createElement('script');
      s2.src = 'https://cdnjs.cloudflare.com/ajax/libs/topojson/3.0.2/topojson.min.js';
      s2.onload = () => renderMap(data);
      document.head.appendChild(s2);
    };
    document.head.appendChild(s);
  }, [mapReady, data]);

  const renderMap = (d) => {
    if (!window.d3 || !window.topojson) return;
    const el = document.getElementById('ahcc-map');
    if (!el) return;
    el.innerHTML = '';
    const STATE_ABBR_TO_NAME = {"AL":"Alabama","AK":"Alaska","AZ":"Arizona","AR":"Arkansas","CA":"California","CO":"Colorado","CT":"Connecticut","DE":"Delaware","FL":"Florida","GA":"Georgia","HI":"Hawaii","ID":"Idaho","IL":"Illinois","IN":"Indiana","IA":"Iowa","KS":"Kansas","KY":"Kentucky","LA":"Louisiana","ME":"Maine","MD":"Maryland","MA":"Massachusetts","MI":"Michigan","MN":"Minnesota","MS":"Mississippi","MO":"Missouri","MT":"Montana","NE":"Nebraska","NV":"Nevada","NH":"New Hampshire","NJ":"New Jersey","NM":"New Mexico","NY":"New York","NC":"North Carolina","ND":"North Dakota","OH":"Ohio","OK":"Oklahoma","OR":"Oregon","PA":"Pennsylvania","RI":"Rhode Island","SC":"South Carolina","SD":"South Dakota","TN":"Tennessee","TX":"Texas","UT":"Utah","VT":"Vermont","VA":"Virginia","WA":"Washington","WV":"West Virginia","WI":"Wisconsin","WY":"Wyoming","DC":"District of Columbia"};
const rawState = d.byState || {};
const stateCounts = {};
Object.keys(rawState).forEach(k => { const name = STATE_ABBR_TO_NAME[k]; if(name) stateCounts[name] = (stateCounts[name]||0) + rawState[k]; });
    const max = Math.max(...Object.values(stateCounts), 1);
    const color = window.d3.scaleSequential().domain([0, Math.log(max+1)]).interpolator(t => t === 0 ? '#111' : t < 0.33 ? '#ffd700' : t < 0.66 ? '#ff6600' : '#ff0000');
      const colorFn = v => (stateCounts[v]||0) === 0 ? '#0d0d0d' : color(Math.log((stateCounts[v]||0)+1));
    const svg = window.d3.select('#ahcc-map').append('svg').attr('viewBox','0 0 960 600').attr('width','100%');
    window.d3.json('https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json').then(us => {
      const path = window.d3.geoPath(window.d3.geoAlbersUsa().scale(1300).translate([480,300]));
      svg.selectAll('path')
        .data(window.topojson.feature(us, us.objects.states).features)
        .join('path')
        .attr('d', path)
        .attr('fill', f => colorFn(f.properties.name))
        .attr('stroke','#0a1a0a')
        .attr('stroke-width',0.5)
        .append('title')
        .text(f => f.properties.name + ': ' + (stateCounts[f.properties.name]||0) + ' visits');
    });
  };

  if (loading && !data) return <div style={{color:"rgba(240,236,226,.4)",padding:"2rem",textAlign:"center"}}>Loading traffic data...</div>;
  if (!data) return <div style={{color:"#e25555",padding:"2rem"}}>Failed to load.</div>;

  const allVisits = data.recent || [];
  const filtered = allVisits.filter(v => {
    const day = v.ts ? v.ts.substring(0,10) : '';
    if (dateFrom && day < dateFrom) return false;
    if (dateTo && day > dateTo) return false;
    return true;
  });
  const sorted = sortDir === 'desc' ? filtered : [...filtered].reverse();

  const allVisitsAll = data.recent || [];
  const filteredStats = (dateFrom || dateTo) ? allVisitsAll.filter(v => {
    const day = v.ts ? v.ts.substring(0,10) : '';
    if (dateFrom && day < dateFrom) return false;
    if (dateTo && day > dateTo) return false;
    return true;
  }) : allVisitsAll;

  const filteredTotal = (dateFrom || dateTo) ? filteredStats.length : data.total;
  const filteredUniqueIPs = new Set(filteredStats.map(v=>v.ip)).size;
  const filteredGoogle = filteredStats.filter(v=>v.isGoogle).length;
  const filteredMobile = filteredStats.filter(v=>v.isMobile).length;

  const byDay = data.byDay || {};
  const dayKeys = Object.keys(byDay).sort();
  const googlePct = filteredTotal ? Math.round((filteredGoogle/filteredTotal)*100) : 0;
  const mobilePct = filteredTotal ? Math.round((filteredMobile/filteredTotal)*100) : 0;

  return (<>
    <div className="adm-t">Web Traffic</div>
    <div className="adm-s">All-time historical data — collected hourly. Auto-refreshes every 60 seconds.</div>

    <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"1.5rem",padding:".6rem 1rem",background:"rgba(92,184,92,.07)",border:"1px solid rgba(92,184,92,.2)",borderRadius:"4px"}}>
      <div style={{width:8,height:8,borderRadius:"50%",background:"#5cb85c",animation:"pulse 1.5s infinite"}}></div>
      <span style={{fontSize:".82rem",color:"#5cb85c",fontWeight:500}}>{liveCount} visitor{liveCount!==1?"s":""} on site right now</span>
      <button onClick={load} style={{marginLeft:"auto",background:"none",border:"1px solid rgba(255,255,255,.1)",borderRadius:"3px",color:"rgba(240,236,226,.5)",fontSize:".7rem",padding:".25rem .6rem",cursor:"pointer"}}>↻ Refresh</button>
    </div>

    <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"12px",marginBottom:"1.5rem"}}>
      {hourly && <div style={{background:"rgba(255,255,255,.02)",border:"1px solid rgba(255,255,255,.07)",borderRadius:"4px",padding:"1rem",marginBottom:"1.5rem"}}>
      <div style={{fontSize:".72rem",fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:"rgba(240,236,226,.35)",marginBottom:".75rem"}}>Traffic & Conversions by Hour (CST)</div>
      <div style={{display:"flex",alignItems:"flex-end",gap:"3px",height:100,paddingBottom:"1.5rem",position:"relative"}}>
        {Object.entries(hourly).map(([hr, val]) => {
          const maxV = Math.max(...Object.values(hourly).map(v=>v.visits), 1);
          const visitH = Math.round((val.visits/maxV)*100);
          const convRate = val.visits > 0 ? ((val.directions/val.visits)*100) : 0;
          return (
            <div key={hr} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2,position:"relative"}}>
              <div style={{width:"100%",position:"relative",height:100,display:"flex",flexDirection:"column",justifyContent:"flex-end",gap:2}}>
                {val.directions > 0 && <div style={{width:"100%",height:Math.max(Math.round((val.directions/maxV)*100),2)+"%",background:"#c8102e",borderRadius:"2px 2px 0 0",minHeight:2}}></div>}
                <div style={{width:"100%",height:visitH+"%",background:"#7baee8",borderRadius:"2px 2px 0 0",minHeight:val.visits?2:0,opacity:.7}}></div>
              </div>
              <div style={{fontSize:".5rem",color:"rgba(240,236,226,.25)",position:"absolute",bottom:-16,whiteSpace:"nowrap"}}>{hr}h</div>
            </div>
          );
        })}
      </div>
      <div style={{display:"flex",gap:"1.5rem",marginTop:".5rem"}}>
        <div style={{display:"flex",alignItems:"center",gap:".4rem",fontSize:".68rem",color:"rgba(240,236,226,.4)"}}><div style={{width:10,height:10,background:"#7baee8",borderRadius:2}}></div>Visits</div>
        <div style={{display:"flex",alignItems:"center",gap:".4rem",fontSize:".68rem",color:"rgba(240,236,226,.4)"}}><div style={{width:10,height:10,background:"#c8102e",borderRadius:2}}></div>Directions Clicks</div>
      </div>
    </div>}

    <div style={{display:"flex",gap:"8px",marginBottom:"1rem",alignItems:"center",flexWrap:"wrap"}}>
        <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} style={{background:"rgba(255,255,255,.05)",border:"1px solid rgba(255,255,255,.1)",borderRadius:"3px",color:"rgba(240,236,226,.7)",fontSize:".78rem",padding:".3rem .6rem"}}/>
        <span style={{fontSize:".78rem",color:"rgba(240,236,226,.3)"}}>to</span>
        <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} style={{background:"rgba(255,255,255,.05)",border:"1px solid rgba(255,255,255,.1)",borderRadius:"3px",color:"rgba(240,236,226,.7)",fontSize:".78rem",padding:".3rem .6rem"}}/>
        {(dateFrom||dateTo) && <button onClick={()=>{setDateFrom('');setDateTo('');}} style={{background:"none",border:"1px solid rgba(255,255,255,.1)",borderRadius:"3px",color:"rgba(240,236,226,.4)",fontSize:".72rem",padding:".3rem .6rem",cursor:"pointer"}}>✕ Clear</button>}
        {(dateFrom||dateTo) && <span style={{fontSize:".72rem",color:"rgba(240,236,226,.3)"}}>Showing filtered results</span>}
      </div>

      {[
        {label:"Visits",value:filteredTotal,color:"#7baee8"},
        {label:"Unique IPs",value:filteredUniqueIPs,color:"#a8bfe8"},

        {label:"Mobile",value:filteredMobile,color:"#5cb85c"},
        {label:"From Facebook",value:data.facebookVisits||0,color:"#1877f2"},
      ].map(m=>(
        <div key={m.label} style={{background:"rgba(255,255,255,.03)",border:"1px solid rgba(255,255,255,.07)",borderRadius:"4px",padding:"1rem",textAlign:"center"}}>
          <div style={{fontSize:".65rem",color:"rgba(240,236,226,.4)",textTransform:"uppercase",letterSpacing:".1em",marginBottom:".4rem"}}>{m.label}</div>
          <div style={{fontSize:"1.4rem",fontWeight:600,color:m.color}}>{m.value}</div>
        </div>
      ))}
    </div>


    <div style={{background:"rgba(255,255,255,.02)",border:"1px solid rgba(255,255,255,.07)",borderRadius:"4px",padding:"1rem",marginBottom:"1.5rem"}}>
      <div style={{fontSize:".72rem",fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:"rgba(240,236,226,.35)",marginBottom:".75rem"}}>Daily Visits — Last 14 Days</div>
      <div style={{display:"flex",alignItems:"flex-end",gap:"4px",height:80}}>
        {dayKeys.slice(-14).map(day=>{
          const val = byDay[day]||0;
          const max = Math.max(...dayKeys.slice(-14).map(d=>byDay[d]||0),1);
          const pct = Math.round((val/max)*100);
          return <div key={day} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
            <div style={{width:"100%",height:pct+"%",minHeight:2,background:"#7baee8",borderRadius:"2px 2px 0 0",transition:"height .3s"}}></div>
            <div style={{fontSize:".55rem",color:"rgba(240,236,226,.3)",transform:"rotate(-45deg)",whiteSpace:"nowrap"}}>{day.slice(5)}</div>
          </div>;
        })}
      </div>
    </div>

    <div style={{background:"rgba(0,0,0,.3)",border:"1px solid rgba(0,255,65,.15)",borderRadius:"4px",padding:"1rem",marginBottom:"1.5rem"}}>
      <div style={{fontSize:".72rem",fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:"rgba(0,255,65,.4)",marginBottom:".5rem"}}>U.S. Visitor Heat Map</div>
      <div style={{fontSize:".68rem",color:"rgba(240,236,226,.25)",marginBottom:".75rem"}}>Darker green = more visits from that state</div>
      <div id="ahcc-map" style={{width:"100%",background:"transparent"}}></div>
    </div>

    <div style={{background:"rgba(255,255,255,.02)",border:"1px solid rgba(255,255,255,.07)",borderRadius:"4px",padding:"1rem",marginBottom:"1.5rem"}}>
      <div style={{fontSize:".72rem",fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:"rgba(240,236,226,.35)",marginBottom:".75rem"}}>Traffic Source Breakdown</div>
      {[
        {label:"Google Ads",pct:googlePct,color:"#ffc46b"},
        {label:"Facebook",pct:data.total?Math.round((data.facebookVisits||0)/data.total*100):0,color:"#1877f2"},
        {label:"Mobile Users",pct:mobilePct,color:"#5cb85c"},
        {label:"Desktop Users",pct:100-mobilePct,color:"#7baee8"},
      ].map(b=>(
        <div key={b.label} style={{marginBottom:".6rem"}}>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:".78rem",color:"rgba(240,236,226,.6)",marginBottom:".3rem"}}>
            <span>{b.label}</span><span>{b.pct}%</span>
          </div>
          <div style={{height:6,background:"rgba(255,255,255,.06)",borderRadius:3}}>
            <div style={{height:6,width:b.pct+"%",background:b.color,borderRadius:3,transition:"width .5s"}}></div>
          </div>
        </div>
      ))}
    </div>

    <div style={{background:"rgba(255,255,255,.02)",border:"1px solid rgba(255,255,255,.07)",borderRadius:"4px",padding:"1rem"}}>
      <div style={{display:"flex",alignItems:"center",gap:"12px",marginBottom:".75rem",flexWrap:"wrap"}}>
        <div style={{fontSize:".72rem",fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:"rgba(240,236,226,.35)"}}>Recent Visitors</div>
        <div style={{display:"flex",gap:"8px",marginLeft:"auto",alignItems:"center",flexWrap:"wrap"}}>
          <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} style={{background:"rgba(255,255,255,.05)",border:"1px solid rgba(255,255,255,.1)",borderRadius:"3px",color:"rgba(240,236,226,.7)",fontSize:".72rem",padding:".25rem .5rem"}}/>
          <span style={{fontSize:".72rem",color:"rgba(240,236,226,.3)"}}>to</span>
          <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} style={{background:"rgba(255,255,255,.05)",border:"1px solid rgba(255,255,255,.1)",borderRadius:"3px",color:"rgba(240,236,226,.7)",fontSize:".72rem",padding:".25rem .5rem"}}/>
          <button onClick={()=>setSortDir(d=>d==='desc'?'asc':'desc')} style={{background:"rgba(255,255,255,.05)",border:"1px solid rgba(255,255,255,.1)",borderRadius:"3px",color:"rgba(240,236,226,.6)",fontSize:".72rem",padding:".25rem .6rem",cursor:"pointer"}}>
            {sortDir==='desc'?'↓ Newest':'↑ Oldest'}
          </button>
          <button onClick={()=>{setDateFrom('');setDateTo('');}} style={{background:"none",border:"1px solid rgba(255,255,255,.07)",borderRadius:"3px",color:"rgba(240,236,226,.3)",fontSize:".68rem",padding:".25rem .5rem",cursor:"pointer"}}>Clear</button>
        </div>
      </div>
      <div style={{fontSize:".68rem",color:"rgba(240,236,226,.25)",marginBottom:".5rem"}}>Showing {sorted.length} visits</div>
      <div style={{overflowX:"auto"}}>
        <table style={{width:"100%",fontSize:".73rem",borderCollapse:"collapse"}}>
          <thead>
            <tr style={{color:"rgba(240,236,226,.3)",textAlign:"left"}}>
              <th style={{padding:".4rem .6rem",fontWeight:400}}>Time</th>
              <th style={{padding:".4rem .6rem",fontWeight:400}}>IP</th>
              <th style={{padding:".4rem .6rem",fontWeight:400}}>Page</th>
              <th style={{padding:".4rem .6rem",fontWeight:400}}>Source</th>
              <th style={{padding:".4rem .6rem",fontWeight:400}}>Device</th>
              <th style={{padding:".4rem .6rem",fontWeight:400}}>Status</th>
            </tr>
          </thead>
          <tbody>
            {sorted.slice(0,100).map((v,i)=>(
              <tr key={i} style={{borderTop:"1px solid rgba(255,255,255,.04)"}}>
                <td style={{padding:".4rem .6rem",color:"rgba(240,236,226,.35)",fontFamily:"monospace",fontSize:".68rem"}}>{v.ts?v.ts.substring(0,16).replace('T',' '):''}</td>
                <td style={{padding:".4rem .6rem",color:"rgba(240,236,226,.5)",fontFamily:"monospace"}}>{v.ip}</td>
                <td style={{padding:".4rem .6rem",color:"rgba(240,236,226,.7)",maxWidth:180,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{v.path||"/"}</td>
                <td style={{padding:".4rem .6rem"}}>{v.isGoogle?<span style={{color:"#ffc46b",fontSize:".68rem"}}>Google Ads</span>:v.isFacebook?<span style={{color:"#1877f2",fontSize:".68rem"}}>Facebook</span>:<span style={{color:"rgba(240,236,226,.3)",fontSize:".68rem"}}>Direct</span>}</td>
                <td style={{padding:".4rem .6rem"}}>{v.isMobile?<span style={{color:"#5cb85c",fontSize:".68rem"}}>Mobile</span>:<span style={{color:"#7baee8",fontSize:".68rem"}}>Desktop</span>}</td>
                <td style={{padding:".4rem .6rem"}}><span style={{color:v.status===200?"#5cb85c":"#e25555",fontSize:".68rem"}}>{v.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}`}</style>
  </>);
}

// ─── BOT TAB ─────────────────────────────────────────────────────────────────
function BotTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(()=>{ fetch('/api/traffic-history').then(r=>r.json()).then(d=>{setData(d);setLoading(false);}).catch(()=>setLoading(false)); },[]);
  if (loading) return <div style={{color:"rgba(240,236,226,.4)",padding:"2rem",textAlign:"center"}}>Loading...</div>;
  if (!data) return <div style={{color:"#e25555",padding:"2rem"}}>Failed to load.</div>;
  const reasons = [
    {pattern:"Contains /wp-admin or .php",why:"Automated bots scan all websites for WordPress vulnerabilities, even if you don't run WordPress."},
    {pattern:"Contains /.env",why:"Bots hunt for exposed environment files that may contain passwords or API keys."},
    {pattern:"Contains xmlrpc or .git",why:"Common exploit targets. xmlrpc is a WordPress API; .git exposes source code."},
    {pattern:"Hex characters (\\x00, \\xFF etc)",why:"Malformed requests designed to crash or exploit vulnerable servers."},
  ];
  return (<>
    <div className="adm-t">Bot Activity</div>
    <div className="adm-s">These requests were automatically identified as non-human traffic and excluded from your main traffic stats.</div>

    <div style={{background:"rgba(226,85,85,.07)",border:"1px solid rgba(226,85,85,.2)",borderRadius:"4px",padding:"1rem",marginBottom:"1.5rem"}}>
      <div style={{fontSize:"1.4rem",fontWeight:600,color:"#e25555",marginBottom:".25rem"}}>{data.botCount||0}</div>
      <div style={{fontSize:".72rem",color:"rgba(240,236,226,.4)",textTransform:"uppercase",letterSpacing:".1em"}}>Bot requests detected today</div>
    </div>

    <div style={{background:"rgba(255,255,255,.02)",border:"1px solid rgba(255,255,255,.07)",borderRadius:"4px",padding:"1rem",marginBottom:"1.5rem"}}>
      <div style={{fontSize:".72rem",fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:"rgba(240,236,226,.35)",marginBottom:"1rem"}}>How We Identify Bots</div>
      {reasons.map((r,i)=>(
        <div key={i} style={{marginBottom:".75rem",paddingBottom:".75rem",borderBottom:"1px solid rgba(255,255,255,.05)"}}>
          <div style={{fontSize:".78rem",color:"#ffc46b",fontFamily:"monospace",marginBottom:".25rem"}}>{r.pattern}</div>
          <div style={{fontSize:".73rem",color:"rgba(240,236,226,.5)",lineHeight:1.6}}>{r.why}</div>
        </div>
      ))}
    </div>

    <div style={{background:"rgba(255,255,255,.02)",border:"1px solid rgba(255,255,255,.07)",borderRadius:"4px",padding:"1rem"}}>
      <div style={{fontSize:".72rem",fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:"rgba(240,236,226,.35)",marginBottom:".75rem"}}>Recent Bot Requests</div>
      <div style={{overflowX:"auto"}}>
        <table style={{width:"100%",fontSize:".73rem",borderCollapse:"collapse"}}>
          <thead>
            <tr style={{color:"rgba(240,236,226,.3)",textAlign:"left"}}>
              <th style={{padding:".4rem .6rem",fontWeight:400}}>Time</th>
              <th style={{padding:".4rem .6rem",fontWeight:400}}>IP</th>
              <th style={{padding:".4rem .6rem",fontWeight:400}}>Request</th>
              <th style={{padding:".4rem .6rem",fontWeight:400}}>Status</th>
            </tr>
          </thead>
          <tbody>
            {(data.botSummary||[]).slice(0,100).map((v,i)=>(
              <tr key={i} style={{borderTop:"1px solid rgba(255,255,255,.04)"}}>
                <td style={{padding:".4rem .6rem",color:"rgba(240,236,226,.35)",fontFamily:"monospace",fontSize:".68rem"}}>{v.ts?v.ts.substring(0,16).replace('T',' '):''}</td>
                <td style={{padding:".4rem .6rem",color:"rgba(240,236,226,.5)",fontFamily:"monospace"}}>{v.ip}</td>
                <td style={{padding:".4rem .6rem",color:"#e25555",maxWidth:300,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontSize:".68rem"}}>{v.path}</td>
                <td style={{padding:".4rem .6rem"}}><span style={{color:v.status===200?"#e25555":"#ffc46b",fontSize:".68rem"}}>{v.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </>);
}

// ─── AMERICAN-HERD.COM TRAFFIC TAB ───────────────────────────────────────────
function AHTrafficTab() {
  const [data, setData] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(new Date().toISOString().substring(0,10));
  const [dateTo, setDateTo] = useState(new Date().toISOString().substring(0,10));
  const [sortDir, setSortDir] = useState('desc');
  const [hourly, setHourly] = useState(null);

  const load = async () => {
    setLoading(true);
    try { await fetch('/api/ah-traffic-collect', { method: 'POST' }); } catch(e) {}
    fetch('/api/ah-traffic').then(r=>r.json()).then(d=>{setData(d);setLoading(false);}).catch(()=>setLoading(false));
    fetch('/api/ah-sessions').then(r=>r.json()).then(d=>setSessions(d)).catch(()=>{});
    fetch('/api/ah-hourly').then(r=>r.json()).then(d=>setHourly(d.byHour)).catch(()=>{});
  };

  useEffect(()=>{ load(); const t=setInterval(load,60000); return ()=>clearInterval(t); },[]);

  if (loading && !data) return <div style={{color:"rgba(240,236,226,.4)",padding:"2rem",textAlign:"center"}}>Loading american-herd.com traffic...</div>;
  if (!data) return <div style={{color:"#e25555",padding:"2rem"}}>No data yet — traffic will appear after the first hourly collection.</div>;

  const allVisits = data.recent || [];
  const filtered = allVisits.filter(v => {
    const day = v.ts ? v.ts.substring(0,10) : '';
    if (dateFrom && day < dateFrom) return false;
    if (dateTo && day > dateTo) return false;
    return true;
  }).filter(v => v.ip !== '99.103.219.83');
  const sorted = sortDir === 'desc' ? filtered : [...filtered].reverse();

  const googlePct = data.total ? Math.round((data.googleVisits/data.total)*100) : 0;
  const fbPct = data.total ? Math.round((data.facebookVisits/data.total)*100) : 0;
  const mobilePct = data.total ? Math.round((data.mobileVisits/data.total)*100) : 0;
  const byDay = data.byDay || {};
  const dayKeys = Object.keys(byDay).sort();

  return (<>
    <div className="adm-t">american-herd.com Traffic</div>
    <div className="adm-s">Separate tracking for your sale landing page site. Collected hourly.</div>

    <div style={{display:"flex",gap:"8px",marginBottom:"1rem",alignItems:"center",flexWrap:"wrap"}}>
      <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} style={{background:"rgba(255,255,255,.05)",border:"1px solid rgba(255,255,255,.1)",borderRadius:"3px",color:"rgba(240,236,226,.7)",fontSize:".78rem",padding:".3rem .6rem"}}/>
      <span style={{fontSize:".78rem",color:"rgba(240,236,226,.3)"}}>to</span>
      <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} style={{background:"rgba(255,255,255,.05)",border:"1px solid rgba(255,255,255,.1)",borderRadius:"3px",color:"rgba(240,236,226,.7)",fontSize:".78rem",padding:".3rem .6rem"}}/>
      {(dateFrom||dateTo) && <button onClick={()=>{setDateFrom('');setDateTo('');}} style={{background:"none",border:"1px solid rgba(255,255,255,.1)",borderRadius:"3px",color:"rgba(240,236,226,.4)",fontSize:".72rem",padding:".3rem .6rem",cursor:"pointer"}}>✕ Clear</button>}
      <button onClick={load} style={{marginLeft:"auto",background:"none",border:"1px solid rgba(255,255,255,.1)",borderRadius:"3px",color:"rgba(240,236,226,.5)",fontSize:".7rem",padding:".25rem .6rem",cursor:"pointer"}}>↻ Refresh</button>
    </div>

    <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"12px",marginBottom:"1.5rem"}}>
      {[
        {label:"All-Time Visits",value:data.total,color:"#7baee8"},
        {label:"Unique IPs",value:data.uniqueIPs,color:"#a8bfe8"},
        {label:"Mobile",value:data.mobileVisits,color:"#5cb85c"},
        {label:"From Facebook",value:data.facebookVisits||0,color:"#1877f2"},
      ].map(m=>(
        <div key={m.label} style={{background:"rgba(255,255,255,.03)",border:"1px solid rgba(255,255,255,.07)",borderRadius:"4px",padding:"1rem",textAlign:"center"}}>
          <div style={{fontSize:".65rem",color:"rgba(240,236,226,.4)",textTransform:"uppercase",letterSpacing:".1em",marginBottom:".4rem"}}>{m.label}</div>
          <div style={{fontSize:"1.4rem",fontWeight:600,color:m.color}}>{m.value}</div>
        </div>
      ))}
    </div>

    <div style={{background:"rgba(255,255,255,.02)",border:"1px solid rgba(255,255,255,.07)",borderRadius:"4px",padding:"1rem",marginBottom:"1.5rem"}}>
      <div style={{fontSize:".72rem",fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:"rgba(240,236,226,.35)",marginBottom:".75rem"}}>Daily Visits — Last 14 Days</div>
      <div style={{display:"flex",alignItems:"flex-end",gap:"4px",height:80}}>
        {dayKeys.slice(-14).map(day=>{
          const val = byDay[day]||0;
          const max = Math.max(...dayKeys.slice(-14).map(d=>byDay[d]||0),1);
          const pct = Math.round((val/max)*100);
          return <div key={day} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
            <div style={{width:"100%",height:pct+"%",minHeight:2,background:"#c8102e",borderRadius:"2px 2px 0 0",transition:"height .3s"}}></div>
            <div style={{fontSize:".55rem",color:"rgba(240,236,226,.3)",transform:"rotate(-45deg)",whiteSpace:"nowrap"}}>{day.slice(5)}</div>
          </div>;
        })}
      </div>
    </div>

    {hourly && <div style={{background:"rgba(255,255,255,.02)",border:"1px solid rgba(255,255,255,.07)",borderRadius:"4px",padding:"1rem",marginBottom:"1.5rem"}}>
      <div style={{fontSize:".72rem",fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:"rgba(240,236,226,.35)",marginBottom:".75rem"}}>Traffic & Conversions by Hour (CST)</div>
      <div style={{display:"flex",alignItems:"flex-end",gap:"3px",height:80,paddingBottom:"1.2rem",position:"relative",marginBottom:".5rem"}}>
        {Object.entries(hourly).map(([hr,val])=>{
          const maxV=Math.max(...Object.values(hourly).map(v=>v.visits),1);
          return <div key={hr} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",position:"relative"}}>
            <div style={{width:"100%",height:80,display:"flex",flexDirection:"column",justifyContent:"flex-end",gap:1}}>
              {val.directions>0&&<div style={{width:"100%",height:Math.max(Math.round((val.directions/maxV)*80),2),background:"#c8102e",borderRadius:"2px 2px 0 0"}}></div>}
              <div style={{width:"100%",height:Math.max(Math.round((val.visits/maxV)*80),val.visits?1:0),background:"#7baee8",borderRadius:"2px 2px 0 0",opacity:.7}}></div>
            </div>
            <div style={{fontSize:".45rem",color:"rgba(240,236,226,.2)",marginTop:2}}>{hr}</div>
          </div>;
        })}
      </div>
      <div style={{display:"flex",gap:"1rem"}}>
        <div style={{display:"flex",alignItems:"center",gap:4,fontSize:".65rem",color:"rgba(240,236,226,.4)"}}><div style={{width:8,height:8,background:"#7baee8",borderRadius:1}}></div>Visits</div>
        <div style={{display:"flex",alignItems:"center",gap:4,fontSize:".65rem",color:"rgba(240,236,226,.4)"}}><div style={{width:8,height:8,background:"#c8102e",borderRadius:1}}></div>Directions</div>
      </div>
    </div>}

        <div style={{background:"rgba(255,255,255,.02)",border:"1px solid rgba(255,255,255,.07)",borderRadius:"4px",padding:"1rem",marginBottom:"1.5rem"}}>
      <div style={{fontSize:".72rem",fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:"rgba(240,236,226,.35)",marginBottom:".75rem"}}>Traffic Sources</div>
      {[
        {label:"Facebook",pct:fbPct,color:"#1877f2"},
        {label:"Google Ads",pct:googlePct,color:"#ffc46b"},
        {label:"Mobile Users",pct:mobilePct,color:"#5cb85c"},
      ].map(b=>(
        <div key={b.label} style={{marginBottom:".6rem"}}>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:".78rem",color:"rgba(240,236,226,.6)",marginBottom:".3rem"}}>
            <span>{b.label}</span><span>{b.pct}%</span>
          </div>
          <div style={{height:6,background:"rgba(255,255,255,.06)",borderRadius:3}}>
            <div style={{height:6,width:b.pct+"%",background:b.color,borderRadius:3,transition:"width .5s"}}></div>
          </div>
        </div>
      ))}
    </div>

    <div style={{background:"rgba(255,255,255,.02)",border:"1px solid rgba(255,255,255,.07)",borderRadius:"4px",padding:"1rem"}}>
      <div style={{display:"flex",alignItems:"center",gap:"12px",marginBottom:".75rem",flexWrap:"wrap"}}>
        <div style={{fontSize:".72rem",fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:"rgba(240,236,226,.35)"}}>Recent Visitors</div>
        <div style={{display:"flex",gap:"8px",marginLeft:"auto",alignItems:"center"}}>
          <button onClick={()=>setSortDir(d=>d==='desc'?'asc':'desc')} style={{background:"rgba(255,255,255,.05)",border:"1px solid rgba(255,255,255,.1)",borderRadius:"3px",color:"rgba(240,236,226,.6)",fontSize:".72rem",padding:".25rem .6rem",cursor:"pointer"}}>
            {sortDir==='desc'?'↓ Newest':'↑ Oldest'}
          </button>
        </div>
      </div>
      <div style={{fontSize:".68rem",color:"rgba(240,236,226,.25)",marginBottom:".5rem"}}>Showing {sorted.length} visits</div>
      <div style={{overflowX:"auto"}}>
        <table style={{width:"100%",fontSize:".73rem",borderCollapse:"collapse"}}>
          <thead>
            <tr style={{color:"rgba(240,236,226,.3)",textAlign:"left"}}>
              <th style={{padding:".4rem .6rem",fontWeight:400}}>Time</th>
              <th style={{padding:".4rem .6rem",fontWeight:400}}>IP</th>
              <th style={{padding:".4rem .6rem",fontWeight:400}}>Page</th>
              <th style={{padding:".4rem .6rem",fontWeight:400}}>Source</th>
              <th style={{padding:".4rem .6rem",fontWeight:400}}>Device</th>
            </tr>
          </thead>
          <tbody>
            {sorted.slice(0,100).map((v,i)=>(
              <tr key={i} style={{borderTop:"1px solid rgba(255,255,255,.04)"}}>
                <td style={{padding:".4rem .6rem",color:"rgba(240,236,226,.35)",fontFamily:"monospace",fontSize:".68rem"}}>{v.ts?v.ts.substring(0,16).replace('T',' '):''}</td>
                <td style={{padding:".4rem .6rem",color:"rgba(240,236,226,.5)",fontFamily:"monospace"}}>{v.ip}</td>
                <td style={{padding:".4rem .6rem",color:"rgba(240,236,226,.7)",maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{v.path||"/"}</td>
                <td style={{padding:".4rem .6rem"}}>
                  {v.isGoogle?<span style={{color:"#ffc46b",fontSize:".68rem"}}>Google Ads</span>:v.isFacebook?<span style={{color:"#1877f2",fontSize:".68rem"}}>Facebook</span>:<span style={{color:"rgba(240,236,226,.3)",fontSize:".68rem"}}>Direct</span>}
                </td>
                <td style={{padding:".4rem .6rem"}}>
                  {v.isMobile?<span style={{color:"#5cb85c",fontSize:".68rem"}}>Mobile</span>:<span style={{color:"#7baee8",fontSize:".68rem"}}>Desktop</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </>);
}

// ─── SESSIONS & CLICKS TAB ───────────────────────────────────────────────────
function AHSessionsTab() {
  const [sessions, setSessions] = useState(null);
  const [clicks, setClicks] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([
      fetch('/api/ah-sessions').then(r=>r.json()),
      fetch('/api/ah-clicks').then(r=>r.json())
    ]).then(([s,cl])=>{ setSessions(s); setClicks(cl); setLoading(false); }).catch(()=>setLoading(false));
  };

  useEffect(()=>{ load(); const t=setInterval(load,15000); return ()=>clearInterval(t); },[]);

  if (loading) return <div style={{color:"rgba(240,236,226,.4)",padding:"2rem",textAlign:"center"}}>Loading...</div>;

  const totalClicks = clicks ? clicks.total : 0;

  const eventLabels = { directions:'📍 Directions', google_calendar:'📅 Google Calendar', apple_calendar:'🍎 Apple Calendar' };

  return (<>
    <div className="adm-t">Sessions & Clicks</div>
    <div className="adm-s">Time on page and button click tracking for american-herd.com sale pages.</div>

    <div style={{display:"flex",justifyContent:"flex-end",marginBottom:"1rem"}}>
      <button onClick={load} style={{background:"none",border:"1px solid rgba(255,255,255,.1)",borderRadius:"3px",color:"rgba(240,236,226,.5)",fontSize:".7rem",padding:".25rem .6rem",cursor:"pointer"}}>↻ Refresh</button>
    </div>

    {/* Time on Page */}
    <div style={{background:"rgba(255,255,255,.02)",border:"1px solid rgba(255,255,255,.07)",borderRadius:"4px",padding:"1rem",marginBottom:"1.5rem"}}>
      <div style={{fontSize:".72rem",fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:"rgba(240,236,226,.35)",marginBottom:"1rem"}}>Time On Page</div>
      {!sessions || sessions.totalSessions === 0 ? (
        <div style={{color:"rgba(240,236,226,.3)",fontSize:".82rem"}}>No session data yet — visitors need to leave a page for data to record.</div>
      ) : (
        <>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"12px",marginBottom:"1rem"}}>
            <div style={{textAlign:"center",background:"rgba(255,255,255,.02)",borderRadius:4,padding:"1rem"}}>
              <div style={{fontSize:"1.8rem",fontWeight:700,color:"#ffc46b"}}>{sessions.avgSeconds >= 60 ? Math.floor(sessions.avgSeconds/60)+"m "+sessions.avgSeconds%60+"s" : sessions.avgSeconds+"s"}</div>
              <div style={{fontSize:".65rem",color:"rgba(240,236,226,.35)",textTransform:"uppercase",letterSpacing:".1em",marginTop:".3rem"}}>Avg Time on Page</div>
            </div>
            <div style={{textAlign:"center",background:"rgba(255,255,255,.02)",borderRadius:4,padding:"1rem"}}>
              <div style={{fontSize:"1.8rem",fontWeight:700,color:"#a8bfe8"}}>{sessions.totalSessions}</div>
              <div style={{fontSize:".65rem",color:"rgba(240,236,226,.35)",textTransform:"uppercase",letterSpacing:".1em",marginTop:".3rem"}}>Sessions Tracked</div>
            </div>
          </div>
          {sessions.pageAvgs && sessions.pageAvgs.length > 0 && (
            <div>
              <div style={{fontSize:".68rem",color:"rgba(240,236,226,.3)",marginBottom:".5rem"}}>By Page</div>
              {sessions.pageAvgs.slice(0,8).map(p=>(
                <div key={p.path} style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:".75rem",padding:".4rem 0",borderBottom:"1px solid rgba(255,255,255,.04)"}}>
                  <span style={{color:"rgba(240,236,226,.6)",fontFamily:"monospace"}}>{p.path}</span>
                  <span style={{color:"#ffc46b",fontWeight:600}}>{p.avg >= 60 ? Math.floor(p.avg/60)+"m "+p.avg%60+"s" : p.avg+"s"} <span style={{color:"rgba(240,236,226,.3)",fontWeight:400}}>({p.count})</span></span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>

    {/* Button Clicks */}
    <AHConversionBar traffic={null} clicks={totalClicks} />

    

    <div style={{background:"rgba(255,255,255,.02)",border:"1px solid rgba(255,255,255,.07)",borderRadius:"4px",padding:"1rem",marginBottom:"1.5rem"}}>
      <div style={{fontSize:".72rem",fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:"rgba(240,236,226,.35)",marginBottom:"1rem"}}>Button Clicks</div>
      {!clicks || clicks.total === 0 ? (
        <div style={{color:"rgba(240,236,226,.3)",fontSize:".82rem"}}>No clicks tracked yet.</div>
      ) : (
        <>
          <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:"10px",marginBottom:"1.25rem"}}>
            {Object.entries(clicks.byEvent||{}).filter(([ev])=>ev!=='facebook').map(([event,count])=>(
              <div key={event} style={{background:"rgba(255,255,255,.03)",border:"1px solid rgba(255,255,255,.07)",borderRadius:4,padding:".75rem",textAlign:"center"}}>
                <div style={{fontSize:"1.4rem",fontWeight:700,color:"#5cb85c"}}>{count}</div>
                <div style={{fontSize:".7rem",color:"rgba(240,236,226,.4)",marginTop:".2rem"}}>{eventLabels[event]||event}</div>
              </div>
            ))}
          </div>

          <div style={{fontSize:".68rem",color:"rgba(240,236,226,.3)",marginBottom:".5rem"}}>By Location</div>
          {Object.entries(clicks.byLocation||{}).slice(0,10).map(([loc,events])=>(
            <div key={loc} style={{padding:".5rem 0",borderBottom:"1px solid rgba(255,255,255,.04)"}}>
              <div style={{fontSize:".78rem",fontWeight:600,color:"rgba(240,236,226,.7)",marginBottom:".3rem"}}>{loc}</div>
              <div style={{display:"flex",gap:"1rem",flexWrap:"wrap"}}>
                {Object.entries(events).map(([event,count])=>(
                  <span key={event} style={{fontSize:".72rem",color:"rgba(240,236,226,.45)"}}>{eventLabels[event]||event}: <span style={{color:"#5cb85c",fontWeight:600}}>{count}</span></span>
                ))}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  </>);
}

// ─── CONVERSION BAR ──────────────────────────────────────────────────────────
function AHConversionBar({ traffic, clicks }) {
  const [ahClicks, setAhClicks] = useState(null);
  const [ahTraffic, setAhTraffic] = useState(null);

  useEffect(() => {
    if (clicks === undefined || clicks === null) {
      fetch('/api/ah-clicks').then(r=>r.json()).then(d=>setAhClicks(d.total||0)).catch(()=>{});
    } else {
      setAhClicks(clicks);
    }
    if (traffic === null) {
      fetch('/api/ah-traffic').then(r=>r.json()).then(d=>setAhTraffic(d.total||0)).catch(()=>{});
    } else {
      setAhTraffic(traffic);
    }
  }, [traffic, clicks]);

  const [dirClicks, setDirClicks] = useState(null);
  const [sessions, setSessions] = useState(null);

  useEffect(() => {
    fetch('/api/ah-clicks').then(r=>r.json()).then(d=>setDirClicks((d.byEvent&&d.byEvent.directions)||0)).catch(()=>setDirClicks(0));
  }, []);

  if (ahClicks === null || ahTraffic === null || dirClicks === null) return null;
  const rate = ahTraffic > 0 ? ((dirClicks / ahTraffic) * 100).toFixed(1) : '0.0';
  const color = parseFloat(rate) >= 5 ? '#5cb85c' : parseFloat(rate) >= 2 ? '#ffc46b' : '#e25555';

  return (
    <div style={{background:"rgba(255,255,255,.02)",border:"1px solid rgba(255,255,255,.07)",borderRadius:"4px",padding:"1rem",marginBottom:"1.5rem",display:"flex",alignItems:"center",justifyContent:"space-between",gap:"1rem",flexWrap:"wrap"}}>
      <div>
        <div style={{fontSize:".65rem",letterSpacing:".15em",textTransform:"uppercase",color:"rgba(240,236,226,.35)",marginBottom:".3rem"}}>Conversion Rate</div>
        <div style={{fontSize:"2rem",fontWeight:800,color}}>{rate}%</div>
        <div style={{fontSize:".72rem",color:"rgba(240,236,226,.35)",marginTop:".2rem"}}>{dirClicks} directions clicks out of {ahTraffic} total visitors</div>
      </div>
      <div style={{flex:1,minWidth:120}}>
        <div style={{height:8,background:"rgba(255,255,255,.06)",borderRadius:4,overflow:"hidden"}}>
          <div style={{height:8,width:Math.min(parseFloat(rate),100)+"%",background:color,borderRadius:4,transition:"width .5s"}}></div>
        </div>
        <div style={{display:"flex",justifyContent:"space-between",fontSize:".65rem",color:"rgba(240,236,226,.25)",marginTop:".3rem"}}>
          <span>0%</span><span style={{color:parseFloat(rate)>=10?"#5cb85c":"rgba(240,236,226,.25)"}}>10%+ = good</span>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * SUSSY IPs — flags unusual visitors, shows what they did and when,
 * and blocks them behind PIN 1053. Cellular addresses get a hard warning
 * first, because carriers share one address across thousands of phones.
 * ------------------------------------------------------------------ */

function JodieDriving() {
  // Tiny Jodie in the Mustang, one lap a minute. Flat two-tone, no glow.
  return (
    <div style={{position:'relative',height:26,overflow:'hidden',borderBottom:'1px solid rgba(255,255,255,.07)',background:'#0a0a0a'}}>
      <style>{`@keyframes jodieDrive{from{transform:translateX(-70px)}to{transform:translateX(calc(100vw + 70px))}}
               @keyframes jodieBump{0%,100%{transform:translateY(0)}50%{transform:translateY(-1px)}}`}</style>
      <div style={{position:'absolute',top:3,left:0,animation:'jodieDrive 60s linear infinite'}}>
        <div style={{animation:'jodieBump .5s ease-in-out infinite'}}>
          <svg width="62" height="20" viewBox="0 0 124 40" aria-label="Jodie in the Mustang">
            <path d="M6 31 L7 25 L26 23 L46 22 L60 11 L84 11 L98 22 L112 25 L118 27 L118 31 Z" fill="#c8102e"/>
            <path d="M61 13 L83 13 L96 22 L49 22 Z" fill="#1b1b1b"/>
            <path d="M63 15 L74 15 L74 21 L56 21 Z" fill="#8fa3b5"/>
            <path d="M77 15 L82 15 L91 21 L77 21 Z" fill="#8fa3b5"/>
            <path d="M63.2 19.4 Q62.6 12.6 68 12.6 Q73.4 12.6 72.8 19.4 Z" fill="#4a3320"/>
            <circle cx="68" cy="16.4" r="3.1" fill="#f0c9a0"/>
            <path d="M64.9 15.2 Q68 11.4 71.1 15.2 Q71.3 13 68 12.8 Q64.7 13 64.9 15.2 Z" fill="#4a3320"/>
            <rect x="65.6" y="18.9" width="4.8" height="2.4" fill="#f0ece2"/>
            <rect x="10" y="27.4" width="104" height="2" fill="#f0ece2" opacity=".9"/>
            <rect x="110" y="23.4" width="7" height="2.6" fill="#f0a500"/>
            <rect x="6.6" y="25.4" width="5" height="2.6" fill="#ffe9a8"/>
            <circle cx="30" cy="31.5" r="7.2" fill="#0f0f0f"/><circle cx="30" cy="31.5" r="2.9" fill="#9a9a9a"/>
            <circle cx="95" cy="31.5" r="7.2" fill="#0f0f0f"/><circle cx="95" cy="31.5" r="2.9" fill="#9a9a9a"/>
          </svg>
        </div>
      </div>
      <div style={{position:'absolute',bottom:0,left:0,right:0,height:1,background:'rgba(255,255,255,.12)'}}/>
    </div>
  );
}

function SussyButton() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('people');
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmIp, setConfirmIp] = useState(null);   // { ip, warning } when cellular
  const [pin, setPin] = useState('');
  const [note, setNote] = useState('');

  const load = () => {
    setErr(''); setData(null);
    fetch('/api/ah-sussy-v2')
      .then(r => r.json())
      .then(d => d.ok ? setData(d) : setErr(d.error || 'could not load'))
      .catch(e => setErr(e.message));
  };
  useEffect(() => { if (open) load(); }, [open]);

  const flagged = data ? (data.people.length + data.scanners.length) : null;

  const doBlock = async (ip, confirmCellular) => {
    if (pin !== '1053') { setErr('Enter the PIN to block.'); return; }
    setBusy(true); setErr('');
    try {
      const r = await fetch('/api/ah-block-ip', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ip, pin, confirmCellular, note })
      });
      const j = await r.json();
      if (j.needsConfirm) { setConfirmIp({ ip, warning: j.warning }); }
      else if (!j.ok) { setErr(j.error || 'block failed'); }
      else { setConfirmIp(null); setNote(''); load(); }
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const doUnblock = async (ip) => {
    if (pin !== '1053') { setErr('Enter the PIN to unblock.'); return; }
    setBusy(true);
    try {
      await fetch('/api/ah-unblock-ip', { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ip, pin }) });
      load();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const connColor = c => c === 'cellular' ? '#f0a500' : c === 'datacenter' ? '#7baee8' : '#5cb85c';
  const lbl = { fontSize: '.55rem', letterSpacing: '.08em', color: 'rgba(240,236,226,.4)', textTransform: 'uppercase' };

  const Row = (r) => {
    const isOpen = expanded === r.ip;
    return (
      <div key={r.ip} style={{borderBottom:'1px solid rgba(255,255,255,.06)',padding:'.7rem .9rem',
                              background: r.blocked ? 'rgba(200,16,46,.07)' : 'transparent'}}>
        <div style={{display:'flex',alignItems:'center',gap:'.6rem',flexWrap:'wrap'}}>
          <span style={{fontFamily:'ui-monospace,Menlo,monospace',fontSize:'.8rem',fontWeight:700}}>{r.ip}</span>
          <span style={{background:'#c8102e',color:'#fff',borderRadius:3,fontSize:'.55rem',fontWeight:800,padding:'.12rem .4rem'}}>
            RISK {r.score}
          </span>
          <span style={{border:`1px solid ${connColor(r.connection)}`,color:connColor(r.connection),
                        borderRadius:3,fontSize:'.55rem',fontWeight:700,padding:'.12rem .4rem',textTransform:'uppercase'}}>
            {r.connection}
          </span>
          {r.blocked && <span style={{background:'#c8102e',color:'#fff',borderRadius:3,fontSize:'.55rem',fontWeight:800,padding:'.12rem .4rem'}}>BLOCKED</span>}
          <span style={{fontSize:'.7rem',color:'rgba(240,236,226,.6)'}}>
            {r.city ? `${r.city}, ${r.region}` : 'location unknown'} · {r.devices.join('/')}{r.models.length ? ` (${r.models.join(', ')})` : ''}
          </span>
          <span style={{marginLeft:'auto',fontSize:'.7rem',color:'rgba(240,236,226,.5)'}}>
            {r.visits} views · {r.pageCount} pages · {r.days}d
          </span>
        </div>

        <div style={{fontSize:'.75rem',color:'rgba(240,236,226,.78)',marginTop:'.4rem',lineHeight:1.5}}>{r.summary}</div>

        <div style={{display:'flex',gap:'.35rem',flexWrap:'wrap',marginTop:'.45rem'}}>
          {r.why.map((w,i)=>(
            <span key={i} style={{background:'rgba(255,255,255,.06)',borderRadius:3,fontSize:'.6rem',
                                  color:'rgba(240,236,226,.7)',padding:'.15rem .4rem'}}>{w}</span>
          ))}
        </div>

        <div style={{display:'flex',gap:'.5rem',marginTop:'.55rem',alignItems:'center'}}>
          <button onClick={()=>setExpanded(isOpen ? null : r.ip)}
            style={{background:'none',border:'1px solid rgba(255,255,255,.15)',borderRadius:4,
                    color:'rgba(240,236,226,.7)',fontSize:'.62rem',padding:'.22rem .55rem',cursor:'pointer'}}>
            {isOpen ? 'hide activity' : `activity (${r.timeline.length})`}
          </button>
          {r.blocked
            ? <button disabled={busy} onClick={()=>doUnblock(r.ip)}
                style={{background:'none',border:'1px solid #5cb85c',borderRadius:4,color:'#5cb85c',
                        fontSize:'.62rem',padding:'.22rem .55rem',cursor:'pointer'}}>unblock</button>
            : <button disabled={busy} onClick={()=>doBlock(r.ip, false)}
                style={{background:'none',border:'1px solid #c8102e',borderRadius:4,color:'#c8102e',
                        fontSize:'.62rem',padding:'.22rem .55rem',cursor:'pointer'}}>block this IP</button>}
          {r.cellular && !r.blocked &&
            <span style={{fontSize:'.6rem',color:'#f0a500'}}>shared carrier address — blocking hits other people</span>}
        </div>

        {isOpen && (
          <div style={{marginTop:'.6rem',borderLeft:'2px solid rgba(255,255,255,.12)',paddingLeft:'.7rem',
                       maxHeight:260,overflowY:'auto'}}>
            {r.timeline.map((t,i)=>(
              <div key={i} style={{display:'flex',gap:'.6rem',fontSize:'.68rem',padding:'.13rem 0',
                                   color: t.kind==='action' ? '#f0a500' : 'rgba(240,236,226,.6)'}}>
                <span style={{minWidth:104,fontFamily:'ui-monospace,Menlo,monospace'}}>{t.when}</span>
                <span>{t.what}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <button onClick={()=>setOpen(true)} title="Unusual visitors"
        style={{background:'none',border:'1px solid rgba(200,16,46,.55)',borderRadius:4,color:'#c8102e',
                fontSize:'.65rem',fontWeight:800,letterSpacing:'.05em',padding:'.2rem .5rem',cursor:'pointer',
                marginRight:'.35rem'}}>
        SUSSY IP&#39;S{flagged !== null ? ` (${flagged})` : ''}
      </button>

      {open && (
        <div onClick={()=>setOpen(false)}
          style={{position:'fixed',inset:0,background:'rgba(0,0,0,.72)',zIndex:200,display:'flex',
                  alignItems:'flex-start',justifyContent:'center',padding:'2.5rem 1rem'}}>
          <div onClick={e=>e.stopPropagation()}
            style={{background:'#111',border:'1px solid rgba(255,255,255,.12)',borderRadius:8,width:'100%',
                    maxWidth:920,maxHeight:'88vh',display:'flex',flexDirection:'column',overflow:'hidden'}}>

            <JodieDriving/>

            <div style={{display:'flex',alignItems:'center',gap:'.7rem',padding:'.7rem .9rem',
                         borderBottom:'1px solid rgba(255,255,255,.08)'}}>
              <div style={{fontWeight:900,letterSpacing:'.05em'}}>SUSSY <span style={{color:'#c8102e'}}>IP&#39;S</span></div>
              <span style={{...lbl}}>
                {data ? `${data.scanned} visitors scanned this week` : 'scanning…'}
              </span>
              <button onClick={load} style={{marginLeft:'auto',background:'none',border:'1px solid rgba(255,255,255,.15)',
                borderRadius:4,color:'rgba(240,236,226,.6)',fontSize:'.62rem',padding:'.22rem .55rem',cursor:'pointer'}}>rescan</button>
              <button onClick={()=>setOpen(false)} style={{background:'none',border:'1px solid rgba(255,255,255,.15)',
                borderRadius:4,color:'rgba(240,236,226,.6)',fontSize:'.62rem',padding:'.22rem .55rem',cursor:'pointer'}}>close</button>
            </div>

            <div style={{display:'flex',gap:'.4rem',padding:'.6rem .9rem',borderBottom:'1px solid rgba(255,255,255,.08)',
                         alignItems:'center',flexWrap:'wrap'}}>
              {[['people','People'],['scanners','Bots & scanners'],['blocked','Blocked']].map(([k,label])=>(
                <button key={k} onClick={()=>setTab(k)}
                  style={{background: tab===k ? '#c8102e' : 'none',border:'1px solid rgba(255,255,255,.15)',
                          borderRadius:4,color: tab===k ? '#fff' : 'rgba(240,236,226,.6)',fontSize:'.63rem',
                          fontWeight:700,padding:'.25rem .6rem',cursor:'pointer'}}>
                  {label}{data ? ` ${k==='people'?data.people.length:k==='scanners'?data.scanners.length:data.blocked.length}` : ''}
                </button>
              ))}
              <div style={{marginLeft:'auto',display:'flex',gap:'.4rem',alignItems:'center'}}>
                <input value={note} onChange={e=>setNote(e.target.value)} placeholder="reason (optional)"
                  style={{background:'#0a0a0a',border:'1px solid rgba(255,255,255,.15)',borderRadius:4,
                          color:'#f0ece2',fontSize:'.63rem',padding:'.25rem .5rem',width:150}}/>
                <input value={pin} onChange={e=>setPin(e.target.value)} placeholder="PIN" type="password"
                  style={{background:'#0a0a0a',border:`1px solid ${pin==='1053'?'#5cb85c':'rgba(255,255,255,.15)'}`,
                          borderRadius:4,color:'#f0ece2',fontSize:'.63rem',padding:'.25rem .5rem',width:60}}/>
              </div>
            </div>

            {err && <div style={{background:'rgba(200,16,46,.15)',color:'#ff8b8b',fontSize:'.7rem',padding:'.5rem .9rem'}}>{err}</div>}

            {confirmIp && (
              <div style={{background:'rgba(240,165,0,.12)',borderBottom:'1px solid rgba(240,165,0,.4)',padding:'.8rem .9rem'}}>
                <div style={{color:'#f0a500',fontWeight:800,fontSize:'.72rem',marginBottom:'.3rem'}}>CELLULAR ADDRESS — READ THIS FIRST</div>
                <div style={{fontSize:'.72rem',color:'rgba(240,236,226,.85)',lineHeight:1.5}}>{confirmIp.warning}</div>
                <div style={{display:'flex',gap:'.5rem',marginTop:'.6rem'}}>
                  <button disabled={busy} onClick={()=>doBlock(confirmIp.ip, true)}
                    style={{background:'#c8102e',border:'none',borderRadius:4,color:'#fff',fontSize:'.65rem',
                            fontWeight:700,padding:'.3rem .7rem',cursor:'pointer'}}>Block it anyway</button>
                  <button onClick={()=>setConfirmIp(null)}
                    style={{background:'none',border:'1px solid rgba(255,255,255,.2)',borderRadius:4,
                            color:'rgba(240,236,226,.7)',fontSize:'.65rem',padding:'.3rem .7rem',cursor:'pointer'}}>Leave it alone</button>
                </div>
              </div>
            )}

            <div style={{overflowY:'auto',flex:1}}>
              {!data && !err && <div style={{padding:'1.6rem .9rem',color:'rgba(240,236,226,.5)',fontSize:'.75rem'}}>Reading this week&#39;s logs…</div>}
              {data && tab==='people' && (data.people.length
                ? data.people.map(Row)
                : <div style={{padding:'1.6rem .9rem',color:'rgba(240,236,226,.5)',fontSize:'.75rem'}}>Nothing unusual from a real connection this week.</div>)}
              {data && tab==='scanners' && (data.scanners.length
                ? data.scanners.map(Row)
                : <div style={{padding:'1.6rem .9rem',color:'rgba(240,236,226,.5)',fontSize:'.75rem'}}>No datacenter traffic flagged.</div>)}
              {data && tab==='blocked' && (data.blocked.length
                ? data.blocked.map(b=>(
                    <div key={b.ip} style={{borderBottom:'1px solid rgba(255,255,255,.06)',padding:'.7rem .9rem',
                                            display:'flex',alignItems:'center',gap:'.6rem',flexWrap:'wrap'}}>
                      <span style={{fontFamily:'ui-monospace,Menlo,monospace',fontSize:'.8rem',fontWeight:700}}>{b.ip}</span>
                      <span style={{border:`1px solid ${connColor(b.connection)}`,color:connColor(b.connection),borderRadius:3,
                                    fontSize:'.55rem',fontWeight:700,padding:'.12rem .4rem',textTransform:'uppercase'}}>{b.connection}</span>
                      <span style={{fontSize:'.7rem',color:'rgba(240,236,226,.6)'}}>
                        {b.city ? `${b.city}, ${b.region} · ` : ''}{b.isp} · blocked {b.atCT || b.at}{b.note ? ` · ${b.note}` : ''}
                      </span>
                      <button disabled={busy} onClick={()=>doUnblock(b.ip)} style={{marginLeft:'auto',background:'none',
                        border:'1px solid #5cb85c',borderRadius:4,color:'#5cb85c',fontSize:'.62rem',
                        padding:'.22rem .55rem',cursor:'pointer'}}>unblock</button>
                    </div>))
                : <div style={{padding:'1.6rem .9rem',color:'rgba(240,236,226,.5)',fontSize:'.75rem'}}>Nothing blocked.</div>)}
            </div>

            <div style={{borderTop:'1px solid rgba(255,255,255,.08)',padding:'.5rem .9rem',...lbl}}>
              Flags repeat visits, several different sale pages, out-of-area addresses and traffic that never came from an ad.
              Blocking needs the PIN and goes live within 2 minutes.
            </div>
          </div>
        </div>
      )}
    </>
  );
}
